// frontend/src/components/remediation/FixPlanModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAdvisor } from "../../AdvisorContext";
import {
  buildControlLabel,
  cleanValue,
  formatCredentialTimestamp,
  formatTimestamp,
  getCredentialStatusLabel,
  getFixPlanDisplayState,
  getFixPlanDisplayStateTone,
  getTaskStatusLabel,
  looksLikeUuid,
  normalizeText,
} from "../../utils/complianceFormatters";

function MetricCard({ label, value, footnote, tone = "default" }) {
  const toneStyle =
    tone === "danger"
      ? { color: "var(--danger)" }
      : tone === "warn"
      ? { color: "var(--warn)" }
      : tone === "ok"
      ? { color: "var(--ok)" }
      : {};

  return (
    <div className="hero-kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value small" style={toneStyle}>
        {value}
      </div>
      {footnote ? <div className="hero-kpi-foot">{footnote}</div> : null}
    </div>
  );
}

function MetaField({ label, value, wrap = false }) {
  return (
    <div className="open-item-pill" style={{ cursor: "default", alignItems: "flex-start" }}>
      <div className="open-item-label">{label}</div>
      <div
        className="open-item-value"
        style={{
          fontSize: 12,
          maxWidth: "100%",
          textAlign: "right",
          whiteSpace: wrap ? "normal" : "nowrap",
          overflow: wrap ? "visible" : "hidden",
          textOverflow: wrap ? "clip" : "ellipsis",
          wordBreak: wrap ? "break-word" : "normal",
          lineHeight: 1.35,
        }}
        title={String(value || "—")}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function getSopStatusLabel(sop = {}) {
  const status = cleanValue(sop.status, "unknown");
  const versionStatus = cleanValue(sop.current_version_status, "");
  const releaseStatus = cleanValue(sop.release_status, "");

  const parts = [status];

  if (versionStatus && normalizeText(versionStatus) !== normalizeText(status)) {
    parts.push(versionStatus);
  }

  if (releaseStatus) {
    parts.push(releaseStatus.replace(/_/g, " "));
  }

  return parts.join(" / ");
}

function isReleasedGoverningSop(sop = {}) {
  return Boolean(sop.is_effective) || normalizeText(sop.release_status) === "effective";
}

function getSopLinkScope(sop = {}, fallbackFramework = "") {
  const link = sop.link || {};
  const parts = [
    cleanValue(link.framework || sop.framework || fallbackFramework, ""),
    cleanValue(link.control_id, ""),
    cleanValue(link.rule_id, ""),
  ].filter(Boolean);

  return parts.length ? parts.join(" · ") : "Linked control context";
}

function getSopReleaseGuidance(sop = {}) {
  if (isReleasedGoverningSop(sop)) {
    return "This SOP is linked and released. Use it as the controlled remediation procedure for this risk.";
  }

  return "Linked SOP found, but it is not yet released. Release this SOP before treating it as controlled remediation guidance.";
}


function getPrimarySopId(fixPlanRelatedSops = [], relatedSopRemediation = {}) {
  const primary =
    fixPlanRelatedSops?.[0] ||
    relatedSopRemediation?.primary_sop ||
    relatedSopRemediation?.remediation_card?.which_sop_applies ||
    null;

  return (
    primary?.sop_id ||
    primary?.id ||
    relatedSopRemediation?.remediation_card?.open_sop_id ||
    null
  );
}

function getTaskId(task = {}) {
  return (
    task?.id ||
    task?.task_id ||
    task?.taskId ||
    task?.remediation_task_id ||
    task?.remediationTaskId ||
    task?.raw?.id ||
    task?.raw?.task_id ||
    ""
  );
}


function normalizeEvidencePayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.evidence)) return payload.evidence;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getTaskEvidenceCount(task = {}) {
  const direct = [
    task?.evidence_count,
    task?.evidenceCount,
    task?.linked_evidence_count,
    task?.linkedEvidenceCount,
    task?.evidence_total,
    task?.evidenceTotal,
    task?.total_evidence,
    task?.totalEvidence,
    task?.task_metadata?.evidence_count,
    task?.task_metadata?.linked_evidence_count,
    task?.metadata?.evidence_count,
    task?.metadata?.linked_evidence_count,
  ];

  for (const value of direct) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric >= 0) return numeric;
  }

  const arrays = [
    task?.evidence,
    task?.linked_evidence,
    task?.linkedEvidence,
    task?.evidence_items,
    task?.evidenceItems,
    task?.task_metadata?.evidence,
    task?.metadata?.evidence,
  ];

  for (const value of arrays) {
    if (Array.isArray(value)) return value.length;
  }

  return null;
}

function getAppliedCopilotDraftFromTask(task = {}) {
  const copilotDraft = task?.task_metadata?.copilot_draft || null;
  if (!copilotDraft || typeof copilotDraft !== "object") return null;

  return {
    title: task?.title || "Applied Copilot remediation plan",
    investigation_notes:
      task?.description ||
      copilotDraft?.investigation_notes ||
      "This Copilot remediation draft has been applied to the tracked remediation task.",
    recommended_actions: Array.isArray(task?.remediation_steps)
      ? task.remediation_steps
      : Array.isArray(copilotDraft?.recommended_actions)
      ? copilotDraft.recommended_actions
      : [],
    evidence_checklist: Array.isArray(copilotDraft?.evidence_checklist)
      ? copilotDraft.evidence_checklist
      : [],
    validation_steps: Array.isArray(copilotDraft?.validation_steps)
      ? copilotDraft.validation_steps
      : [],
    risk_notes: Array.isArray(copilotDraft?.risk_notes) ? copilotDraft.risk_notes : [],
    pathway_id: copilotDraft?.pathway_id || null,
    pathway_name: copilotDraft?.pathway_name || null,
    draft_metadata: copilotDraft?.draft_metadata || {},
    linked_sop_id: task?.linked_sop_id || null,
    applied_from_task: true,
  };
}

function getApiBaseUrl(explicitBase = "") {
  const cleaned = String(explicitBase || "").trim().replace(/\/+$/, "");
  if (cleaned) return cleaned;

  if (typeof window !== "undefined" && window.__V1_BASE) {
    return String(window.__V1_BASE || "").trim().replace(/\/+$/, "");
  }

  throw new Error(
    "OASYS API base is unavailable. App runtime configuration must initialize the API authority."
  );
}

async function getCopilotToken(getAccessToken) {
  if (typeof getAccessToken === "function") {
    return await getAccessToken();
  }

  if (typeof window !== "undefined") {
    if (typeof window.__getApiToken__ === "function") {
      return await window.__getApiToken__();
    }

    if (typeof window.__APP_GET_TOKEN === "function") {
      return await window.__APP_GET_TOKEN();
    }
  }

  return null;
}

const APPROVED_EXCEPTION_INPUT_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 42,
  border: "1px solid color-mix(in srgb, var(--line), white 10%)",
  background: "color-mix(in srgb, var(--panel), white 4%)",
  color: "var(--text)",
  borderRadius: 12,
  padding: "10px 12px",
  outline: "none",
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1.35,
  boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--accent), transparent 92%)",
};

const APPROVED_EXCEPTION_TEXTAREA_STYLE = {
  ...APPROVED_EXCEPTION_INPUT_STYLE,
  resize: "vertical",
  minHeight: 82,
  fontWeight: 750,
};

function DraftList({ title, items = [] }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div
        style={{
          color: "var(--muted)",
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: ".05em",
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
      <ul
        style={{
          margin: 0,
          paddingLeft: "1.1rem",
          color: "var(--text)",
          fontSize: 12,
          lineHeight: 1.55,
          display: "grid",
          gap: 4,
        }}
      >
        {items.map((item, index) => (
          <li key={`${title}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}
function getRulePlanTone(diagnostic = {}) {
  if (diagnostic?.is_generic_fallback) return "accent";
  return "ok";
}

function getRulePlanColor(tone = "ok") {
  if (tone === "warn") return "var(--warn)";
  if (tone === "danger") return "var(--danger)";
  if (tone === "accent") return "var(--accent)";
  return "var(--ok)";
}



function isS3PostureRule(ruleId = "", resourceType = "") {
  const rule = String(ruleId || "").toUpperCase();
  const type = normalizeText(resourceType);

  return (
    type === "s3_bucket" ||
    rule === "S3_VERSIONING" ||
    rule === "S3_LOGGING" ||
    rule === "S3_ENCRYPTION" ||
    rule === "S3_PUBLIC_ACCESS_BLOCK" ||
    rule === "S3_POLICY_PUBLIC" ||
    rule.includes("CONFIG_S3_")
  );
}

function getS3SettingLabel(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  if (rule.includes("VERSIONING")) return "Bucket Versioning";
  if (rule.includes("LOGGING")) return "Server Access Logging";
  if (rule.includes("ENCRYPTION")) return "Default Encryption";
  if (rule.includes("PUBLIC_ACCESS_BLOCK")) return "Block Public Access";
  if (rule.includes("POLICY_PUBLIC")) return "Bucket Policy Public Access";
  return "S3 Control Setting";
}

function getS3ExpectedStateLabel(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  if (rule.includes("VERSIONING")) return "Enabled";
  if (rule.includes("LOGGING")) return "Configured";
  if (rule.includes("ENCRYPTION")) return "Enabled";
  if (rule.includes("PUBLIC_ACCESS_BLOCK")) return "All public access blocked";
  if (rule.includes("POLICY_PUBLIC")) return "Not public";
  return "Expected control state";
}

function getS3PassedEvidenceText(ruleId = "", bucketName = "") {
  const setting = getS3SettingLabel(ruleId).toLowerCase();
  const bucket = cleanValue(bucketName, "the affected bucket");

  if (String(ruleId || "").toUpperCase().includes("VERSIONING")) {
    return `Latest AWS S3 posture shows bucket versioning is enabled for ${bucket}.`;
  }

  return `Latest AWS S3 posture shows ${setting} now satisfies the expected control state for ${bucket}.`;
}


function looksLikeServiceIdentity(value = "") {
  const name = normalizeText(value);

  if (!name) return false;

  return (
    name.includes("service") ||
    name.includes("svc") ||
    name.includes("automation") ||
    name.includes("bot") ||
    name.includes("worker") ||
    name.includes("collector") ||
    name.includes("sync") ||
    name.includes("pipeline") ||
    name.includes("integration") ||
    name.includes("lambda") ||
    name.includes("app")
  );
}

function isIamUnusedRoleRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();
  return rule.includes("IAM_ROLE_UNUSED_90D") || rule.includes("ROLE_UNUSED_90D");
}

function isIamTrustPolicyRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  return (
    rule.includes("ROLE_ASSUME_EXTERNAL_ACCOUNT") ||
    rule.includes("IAM_ROLE_TRUST_POLICY_EXTERNAL_ACCOUNT") ||
    rule.includes("TRUST_POLICY_EXTERNAL_ACCOUNT") ||
    (rule.includes("TRUST") && rule.includes("EXTERNAL"))
  );
}

function isIamPolicyEventRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  return (
    rule.includes("INLINE_POLICY_ADDED_EVENT") ||
    rule.includes("ATTACHED_ROLE_POLICY_EVENT") ||
    rule.includes("ATTACH_ROLE_POLICY") ||
    rule.includes("ATTACH_USER_POLICY") ||
    rule.includes("ATTACH_GROUP_POLICY") ||
    rule.includes("CLOUDTRAIL_EVENT_ATTACHROLEPOLICY") ||
    rule.includes("CLOUDTRAIL_EVENT_ATTACHUSERPOLICY") ||
    rule.includes("CLOUDTRAIL_EVENT_ATTACHGROUPPOLICY") ||
    (rule.includes("POLICY") && (rule.includes("ATTACH") || rule.includes("INLINE")))
  );
}

function cleanIamRoleName(value = "") {
  const cleaned = cleanValue(value, "");
  if (!cleaned || cleaned === "—") return "";
  if (cleaned.startsWith("role:")) return cleaned.slice("role:".length);
  if (cleaned.includes(":role/")) return cleaned.split(":role/").pop();
  if (cleaned.includes("/")) return cleaned.split("/").pop();
  return cleaned;
}

function cleanIamIdentityName(value = "") {
  const cleaned = cleanValue(value, "");
  if (!cleaned || cleaned === "—") return "";
  if (cleaned.startsWith("user:")) return cleaned.slice("user:".length);
  if (cleaned.startsWith("role:")) return cleaned.slice("role:".length);
  if (cleaned.startsWith("group:")) return cleaned.slice("group:".length);
  if (cleaned.includes(":user/")) return cleaned.split(":user/").pop();
  if (cleaned.includes(":role/")) return cleaned.split(":role/").pop();
  if (cleaned.includes(":group/")) return cleaned.split(":group/").pop();
  if (cleaned.includes("/")) return cleaned.split("/").pop();
  return cleaned;
}

function getIamIdentityTarget(awsContext = {}, fallback = "affected IAM identity") {
  return cleanValue(
    cleanIamIdentityName(
      awsContext?.identityName ||
        awsContext?.roleName ||
        awsContext?.userName ||
        awsContext?.groupName ||
        awsContext?.resourceId ||
        awsContext?.roleArn ||
        awsContext?.userArn
    ),
    fallback
  );
}

function getIamCredentialIssueLabel(ruleId = "", awsContext = {}) {
  const rule = String(ruleId || "").toUpperCase();
  const supplied = cleanValue(awsContext?.credentialIssue, "");

  if (supplied && supplied !== "—") return supplied;
  if (rule.includes("NO_MFA") || rule.includes("IAM_USER_MFA")) return "MFA is not enabled";
  if (rule.includes("ACTIVE_KEY") || rule.includes("ACCESS_KEY_1_ACTIVE") || rule.includes("ACCESS_KEY_2_ACTIVE")) return "Access key is active";
  if (rule.includes("OLD_90D")) return "Access key is older than 90 days";
  if (rule.includes("NEVER_USED")) return "Access key has never been used";
  if (rule.includes("PASSWORD_UNUSED")) return "Console password appears unused for 90 days";
  if (rule.includes("USER_UNUSED")) return "IAM user appears unused for 90 days";
  return "IAM credential condition";
}

function getIamDecisionPathToneColor(tone = "default") {
  if (tone === "danger") return "var(--danger)";
  if (tone === "warn") return "var(--warn)";
  if (tone === "ok") return "var(--ok)";
  if (tone === "accent") return "var(--accent)";
  if (tone === "standard") return "var(--info, #60a5fa)";
  if (tone === "primary") return "var(--info, #60a5fa)";
  return "var(--muted)";
}

function buildIamDecisionPath({ label, title, detail, evidence = [], tone = "default" } = {}) {
  return {
    label: cleanValue(label, "Decision Path"),
    title: cleanValue(title, "Review and document the decision path."),
    detail: cleanValue(detail, "Document the decision, supporting evidence, approval, and validation result."),
    evidence: Array.isArray(evidence) ? evidence.filter(Boolean) : [],
    tone,
  };
}

function getDefaultApprovedExceptionReviewDate() {
  const value = new Date();
  value.setDate(value.getDate() + 90);
  return value.toISOString().slice(0, 10);
}

function getApprovedExceptionType(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();
  if (isIamUnusedRoleRule(rule)) return "iam_role_retention";
  if (isIamTrustPolicyRule(rule)) return "iam_trust_retention";
  if (isIamPolicyEventRule(rule)) return "iam_policy_approved_access";
  if (rule.includes("IAM") || rule.includes("ACCESS_KEY") || rule.includes("MFA")) {
    return "iam_credential_temporary_exception";
  }
  return "approved_exception";
}

function getApprovedExceptionDecisionPath(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();
  if (isIamUnusedRoleRule(rule)) return "path_b_retain_as_approved_exception";
  if (isIamTrustPolicyRule(rule)) return "path_b_retain_approved_controlled_trust";
  if (isIamPolicyEventRule(rule)) return "path_b_accept_as_approved_access";
  if (rule.includes("IAM") || rule.includes("ACCESS_KEY") || rule.includes("MFA")) {
    return "path_b_temporary_approved_exception";
  }
  return "path_b_approved_exception";
}

function getTaskLastValidation(task = {}) {
  const metadata = task?.task_metadata || task?.metadata || {};
  return metadata?.last_validation || metadata?.lastValidation || null;
}

function isApprovedExceptionTask(task = {}) {
  const validationStatus = normalizeText(task?.validation_status || task?.validationStatus || "");
  const lastValidation = getTaskLastValidation(task);
  const lastStatus = normalizeText(
    lastValidation?.status ||
      lastValidation?.validation_status ||
      lastValidation?.result ||
      lastValidation?.validation_result ||
      ""
  );
  const governance = normalizeText(
    lastValidation?.metadata?.validation_governance ||
      lastValidation?.metadata?.governance ||
      lastValidation?.metadata?.exception_type ||
      ""
  );
  const decisionPath = normalizeText(lastValidation?.metadata?.decision_path || "");

  return (
    validationStatus === "approved_exception" ||
    lastStatus === "approved_exception" ||
    governance === "approved_exception" ||
    decisionPath.includes("approved_exception") ||
    decisionPath.includes("retain_as_approved_exception")
  );
}

function formatApprovedExceptionLabel(value = "") {
  const cleaned = cleanValue(value, "");
  if (!cleaned) return "—";
  return cleaned
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function buildLocalApprovedExceptionTask(task = {}, payload = {}) {
  const existingMetadata = task?.task_metadata || task?.metadata || {};
  return {
    ...task,
    validation_status: "approved_exception",
    task_metadata: {
      ...existingMetadata,
      last_validation: {
        ...(existingMetadata?.last_validation || {}),
        status: "approved_exception",
        result: "approved_exception",
        reason: payload?.reason || null,
        evidence_ref: payload?.evidence_ref || null,
        validator_name: payload?.validator_name || "Approved Exception",
        validator_type: payload?.validator_type || "exception",
        executed_at: new Date().toISOString(),
        metadata: {
          ...(existingMetadata?.last_validation?.metadata || {}),
          ...(payload?.metadata || {}),
          validation_governance: "approved_exception",
        },
      },
    },
  };
}

function buildDefaultApprovedExceptionForm({
  ruleId = "",
  resourceId = "",
  iamDecisionPanel = null,
  visibleModalTask = null,
  userName = "",
} = {}) {
  const identity = cleanValue(
    iamDecisionPanel?.identityName || resourceId || visibleModalTask?.resource_id,
    "the affected IAM identity"
  );
  const owner = cleanValue(userName || visibleModalTask?.owner_name, "");
  return {
    exception_reason: "",
    business_owner: owner,
    business_purpose: "",
    dependency_notes: "",
    access_advisor_review: "",
    cloudtrail_review: "",
    review_date: getDefaultApprovedExceptionReviewDate(),
    evidence_ref: `manual://approved-exception/${normalizeText(identity || ruleId).replace(/\s+/g, "-") || "iam-exception"}`,
  };
}

function getIamDecisionPanelData({
  ruleId = "",
  awsContext = {},
  isCredentialReport = false,
  isAccessKey = false,
  isActiveAccessKey = false,
  isInlinePolicy = false,
} = {}) {
  const rule = String(ruleId || "").toUpperCase();
  const isUnusedRole = isIamUnusedRoleRule(rule);
  const isTrustPolicy = isIamTrustPolicyRule(rule);
  const isPolicyEvent = isIamPolicyEventRule(rule) || isInlinePolicy;

  if (!isCredentialReport && !isUnusedRole && !isTrustPolicy && !isPolicyEvent) return null;

  if (isTrustPolicy) {
    const roleName = cleanValue(
      cleanIamRoleName(
        awsContext?.roleName ||
          awsContext?.identityName ||
          awsContext?.resourceId ||
          awsContext?.roleArn
      ),
      "affected IAM role"
    );
    const serviceStyle = looksLikeServiceIdentity(roleName);

    return {
      headline: "External trust detected — verify approved account and trust conditions.",
      detail: serviceStyle
        ? "This role name looks workload- or automation-related. Do not remove trust relationships until the trusted account, workload owner, ExternalId or source conditions, and rollback plan are confirmed."
        : "Review the role trust relationship before removing principals or changing assume-role conditions.",
      identityLabel: "IAM Role",
      identityName: roleName,
      classificationLabel: "Trust Classification",
      identityType: serviceStyle ? "Likely workload role" : "Role trust requires review",
      conditionLabel: "Trust Condition",
      condition: "External account or external AssumeRole trust detected",
      decisionLabel: "Safe Decision",
      decision: "Confirm the trusted principal is approved and properly restricted before removing, tightening, or accepting the trust.",
      recommendedPath: [
        "Confirm the business owner and whether this external trust is still required.",
        "Open the role trust relationship and identify every AWS principal, account ID, service principal, and condition block.",
        "Confirm external principals are approved vendors, accounts, workloads, or deployment paths.",
        "Require restrictive conditions where appropriate, such as ExternalId, aws:SourceAccount, aws:SourceArn, MFA, or organization constraints.",
        "Review CloudTrail AssumeRole activity for calling principal, source account, session name, and recent use.",
        "Remove unapproved external principals or add required trust conditions only after owner approval and rollback planning.",
        "Save before/after trust policy evidence, AssumeRole evidence, owner approval, and validation results in the remediation task.",
      ],
      decisionPaths: [
        buildIamDecisionPath({
          label: "Path A",
          title: "Remove or restrict trust",
          tone: "standard",
          detail: "Use when the trusted principal is unapproved, overly broad, missing required conditions, or no longer required.",
          evidence: [
            "Before/after trust policy evidence.",
            "Owner approval and rollback plan.",
            "CloudTrail AssumeRole review showing the trust condition is resolved.",
          ],
        }),
        buildIamDecisionPath({
          label: "Path B",
          title: "Retain approved controlled trust",
          tone: "warn",
          detail: "Use when the external principal is approved and protected with conditions such as ExternalId, SourceAccount, SourceArn, organization restrictions, or MFA.",
          evidence: [
            "Approved principal and business owner evidence.",
            "Trust condition evidence showing control restrictions.",
            "Review date and exception/approval documentation in OASYS.",
          ],
        }),
      ],
    };
  }

  if (isUnusedRole) {
    const roleName = cleanValue(
      cleanIamRoleName(
        awsContext?.roleName ||
          awsContext?.identityName ||
          awsContext?.resourceId ||
          awsContext?.roleArn
      ),
      "affected IAM role"
    );
    const serviceStyle = looksLikeServiceIdentity(roleName);

    return {
      headline: serviceStyle
        ? "Likely workload IAM role — choose deletion or approved retention."
        : "Review IAM role usage before deleting, changing, or retaining access.",
      detail: serviceStyle
        ? "This role name looks workload- or automation-related. Do not delete it until CloudTrail AssumeRole activity, Access Advisor history, owner, and rollback plan are confirmed. If still required, retain it through an approved exception path."
        : "Unused-role findings require an ownership and activity review before deleting, detaching policies, or retaining the role as an approved exception.",
      identityLabel: "IAM Role",
      identityName: roleName,
      classificationLabel: "Role Classification",
      identityType: serviceStyle ? "Likely workload role" : "Unknown until reviewed",
      conditionLabel: "Role Condition",
      condition: "Role appears unused for 90 days",
      decisionLabel: "Safe Decision",
      decision: "Choose the correct path: remove the role if it is no longer required, or retain it as an approved exception with owner, purpose, activity review, and review date documented.",
      recommendedPath: [
        "Confirm the business owner and whether this role is still required by any workload, AWS service, integration, deployment pipeline, or automation.",
        "Review IAM Access Advisor service-last-accessed history for the role.",
        "Review CloudTrail AssumeRole activity for recent use, including calling principal, source account, and session name.",
        "Check whether the role is AWS service-linked, externally assumed, or referenced by application configuration before changing it.",
        "If the role is no longer required, detach unnecessary policies and delete the role only after approval and rollback planning.",
        "If the role must be retained, document the owner, purpose, last-use review, review date, and approved exception in OASYS.",
        "Save owner approval, activity evidence, deletion evidence or exception evidence, and validation results in the remediation task.",
      ],
      decisionPaths: [
        buildIamDecisionPath({
          label: "Path A",
          title: "Remove or delete unused role",
          tone: "standard",
          detail: "Use when the role is no longer required by any workload, AWS service, integration, deployment pipeline, or automation.",
          evidence: [
            "Owner approval to remove the role.",
            "Access Advisor and CloudTrail AssumeRole review evidence.",
            "Before/after role or policy evidence and rollback notes.",
          ],
        }),
        buildIamDecisionPath({
          label: "Path B",
          title: "Retain as approved exception",
          tone: "warn",
          detail: "Use when the role is still required, low-frequency, service-linked, automation-related, or business-approved.",
          evidence: [
            "Decision owner, justification, and dependency documentation.",
            "Access Advisor and CloudTrail review showing why retention is justified.",
            "Approved exception, review date, and supporting evidence attached to the task.",
          ],
        }),
      ],
    };
  }

  if (isPolicyEvent) {
    const identityName = getIamIdentityTarget(awsContext, "affected IAM identity");
    const policyName = cleanValue(
      awsContext?.policyName || awsContext?.policyArn || awsContext?.resourceId,
      "affected policy"
    );
    const eventName = cleanValue(awsContext?.eventName, rule.includes("INLINE") ? "InlinePolicyAdded" : "PolicyAttached");
    const serviceStyle = looksLikeServiceIdentity(identityName);

    return {
      headline: "IAM policy change detected — verify approval before rollback or removal.",
      detail: serviceStyle
        ? "This identity name looks workload- or automation-related. Do not detach or delete policy access until the dependent workload, approval record, and rollback plan are confirmed."
        : "Review the policy change, actor, permissions scope, and approval evidence before detaching, replacing, or accepting the policy.",
      identityLabel: "IAM Identity",
      identityName,
      classificationLabel: "Policy Change Type",
      identityType: eventName,
      conditionLabel: "Policy",
      condition: policyName,
      decisionLabel: "Safe Decision",
      decision: "Confirm the policy attachment or inline policy is approved, least-privilege, and still required before changing access.",
      recommendedPath: [
        "Confirm the business owner and approval record for this IAM policy change.",
        "Review the attached or inline policy document and identify the effective permissions it grants.",
        "Review CloudTrail for the actor, event time, source IP, and change path tied to this policy event.",
        "Confirm whether the identity, workload, group, or role still requires this permission scope.",
        "If access is excessive or unapproved, replace it with a least-privilege policy or detach it after approval and rollback planning.",
        "If access is approved, document the owner, purpose, permission review, and accepted exception or approval evidence in OASYS.",
        "Save before/after policy evidence, CloudTrail evidence, owner approval, and validation results in the remediation task.",
      ],
      decisionPaths: [
        buildIamDecisionPath({
          label: "Path A",
          title: "Roll back or reduce permissions",
          tone: "standard",
          detail: "Use when the policy attachment or inline policy is excessive, unauthorized, no longer needed, or not least-privilege.",
          evidence: [
            "Before/after policy attachment or inline policy evidence.",
            "Approval and rollback/change record.",
            "Validation result showing excessive or unapproved access was removed.",
          ],
        }),
        buildIamDecisionPath({
          label: "Path B",
          title: "Accept as approved access",
          tone: "warn",
          detail: "Use when the policy is approved, least-privilege reviewed, business-required, and documented with owner approval.",
          evidence: [
            "Decision owner / approver evidence.",
            "Permission review or least-privilege justification.",
            "Accepted exception or approval record attached to the task.",
          ],
        }),
      ],
    };
  }

  const userName = cleanValue(
    awsContext?.userName || awsContext?.identityName || awsContext?.resourceId,
    "affected IAM user"
  );
  const serviceStyle = looksLikeServiceIdentity(userName);
  const accessKeySlot = cleanValue(awsContext?.accessKeySlot, "");
  const credentialIssue = getIamCredentialIssueLabel(rule, awsContext);

  const headline = serviceStyle
    ? "Likely service-style IAM user — investigate before changing credentials."
    : "Classify this IAM user before changing credentials.";

  const detail = serviceStyle
    ? "This identity name looks automation- or service-related. Do not deactivate access keys until the dependent workload, secret location, owner, and rollback plan are confirmed."
    : "Confirm whether this is a human console user, a service user, or an unused identity before applying the remediation steps.";

  const recommendedPath = [];

  recommendedPath.push("Confirm the business owner and whether this user is still required.");

  if (rule.includes("NO_MFA") || rule.includes("IAM_USER_MFA")) {
    recommendedPath.push("If the user needs AWS Console access, require and assign an approved MFA device.");
  }

  if (isAccessKey || isActiveAccessKey || rule.includes("ACTIVE_KEY") || rule.includes("ACCESS_KEY")) {
    recommendedPath.push("Identify any workload, secret store, CI/CD variable, or integration using the active access key before rotating or deactivating it.");
    recommendedPath.push("Rotate the key with overlap and monitoring, then deactivate the old key only after the workload is confirmed healthy.");
  }

  if (rule.includes("OLD_90D")) {
    recommendedPath.push("Rotate old access keys using an overlap window, then deactivate the previous key after dependent workloads are confirmed healthy.");
  }

  if (rule.includes("NEVER_USED")) {
    recommendedPath.push("If the access key has never been used, confirm it is not staged for deployment before deactivating and deleting it.");
  }

  if (rule.includes("USER_UNUSED") || rule.includes("PASSWORD_UNUSED")) {
    recommendedPath.push("If the identity is unused, disable console access or remove stale credentials after approval.");
  }

  recommendedPath.push("Prefer migrating long-lived service users to an IAM role or scoped service integration where possible.");
  recommendedPath.push("If a credential must remain temporarily, document owner approval, compensating controls, and review date as an approved exception.");
  recommendedPath.push("Save owner approval, before/after credential evidence, exception evidence if applicable, and validation results in the remediation task.");

  return {
    headline,
    detail,
    identityLabel: "IAM User",
    identityName: userName,
    classificationLabel: "Identity Classification",
    identityType: serviceStyle ? "Likely service user" : "Unknown until classified",
    conditionLabel: accessKeySlot ? `Credential Condition (${accessKeySlot})` : "Credential Condition",
    condition: credentialIssue,
    accessKeySlot,
    decisionLabel: "Safe Decision",
    decision: serviceStyle
      ? "Choose the correct path: remediate the credential, migrate the workload, or retain temporarily with documented approval and compensating controls."
      : "Classify user type, then choose MFA, key rotation, deactivation, role migration, or an approved temporary exception.",
    recommendedPath,
    decisionPaths: [
      buildIamDecisionPath({
        label: "Path A",
        title: "Remediate credential",
        tone: "standard",
        detail: "Use when MFA, key rotation, credential deactivation, unused-user cleanup, or role migration can be completed safely.",
        evidence: [
          "Before/after credential report or IAM user evidence.",
          "Workload dependency and secret location review, if an access key is involved.",
          "Validation result after Full Cloud Assessment confirms the credential condition is resolved.",
        ],
      }),
      buildIamDecisionPath({
        label: "Path B",
        title: "Temporary approved exception",
        tone: "warn",
        detail: "Use only when the credential is still required and cannot be remediated immediately without business impact.",
        evidence: [
          "Decision owner approval and reason for temporary retention.",
          "Compensating controls, expiration/review date, and migration plan if applicable.",
          "Exception documentation attached to the remediation task.",
        ],
      }),
    ],
  };
}

function isIdentityFixPlanRule(ruleId = "", ...items) {
  const rule = String(ruleId || "").toUpperCase();
  const joined = items
    .filter(Boolean)
    .map((item) =>
      [
        item?.source,
        item?.finding_source,
        item?.stream,
        item?.resource_type,
        item?.resourceType,
        item?.raw?.source,
        item?.raw?.stream,
        item?.raw?.resource_type,
      ]
        .filter(Boolean)
        .join(" ")
    )
    .join(" ")
    .toLowerCase();

  return (
    rule.startsWith("GW_") ||
    rule.startsWith("OKTA_") ||
    joined.includes("google_workspace") ||
    joined.includes("okta")
  );
}

function getIdentityProviderLabel(ruleId = "", ...items) {
  const rule = String(ruleId || "").toUpperCase();
  const joined = items
    .filter(Boolean)
    .map((item) => [item?.source, item?.finding_source, item?.stream, item?.raw?.source, item?.raw?.stream].filter(Boolean).join(" "))
    .join(" ")
    .toLowerCase();

  if (rule.startsWith("OKTA_") || joined.includes("okta")) return "Okta";
  if (rule.startsWith("GW_") || joined.includes("google_workspace")) return "Google Workspace";
  return "Identity Provider";
}

function getIdentityRuleLabel(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  const labels = {
    GW_2SV_DISABLED: "Google Workspace 2SV disabled",
    GW_UNUSED_USER: "Google Workspace unused user",
    GW_INACTIVE_ADMIN: "Google Workspace inactive admin",
    GW_ADMIN_COUNT: "Google Workspace admin population",
    GW_SUPER_ADMIN_EXPOSURE: "Google Workspace super admin exposure",
    OKTA_MFA_DISABLED: "Okta MFA disabled",
    OKTA_UNUSED_ACCOUNT: "Okta unused account",
    OKTA_STALE_ADMIN: "Okta stale admin",
    OKTA_ADMIN_EXPOSURE: "Okta admin exposure",
    OKTA_PRIVILEGED_GROUP_EXPOSURE: "Okta privileged group exposure",
  };

  return labels[rule] || rule.replace(/_/g, " ");
}

function getIdentityExpectedAction(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  if (rule === "GW_2SV_DISABLED") return "Enable / enforce 2-Step Verification";
  if (rule === "GW_UNUSED_USER") return "Suspend, archive, or deprovision unused account";
  if (rule === "GW_INACTIVE_ADMIN") return "Remove inactive admin privilege or approve exception";
  if (rule === "GW_ADMIN_COUNT") return "Review and approve admin population";
  if (rule === "GW_SUPER_ADMIN_EXPOSURE") return "Reduce standing Super Admin exposure";
  if (rule === "OKTA_MFA_DISABLED") return "Enroll MFA and enforce sign-on policy";
  if (rule === "OKTA_UNUSED_ACCOUNT") return "Deactivate or suspend unused account";
  if (rule === "OKTA_STALE_ADMIN") return "Remove stale admin privilege or approve exception";
  if (rule === "OKTA_ADMIN_EXPOSURE") return "Review and approve admin population";
  if (rule === "OKTA_PRIVILEGED_GROUP_EXPOSURE") return "Remove unapproved privileged group membership";

  return "Review and remediate identity posture";
}

function getIdentityConsolePath(ruleId = "", providerLabel = "Identity Provider", resourceId = "") {
  const rule = String(ruleId || "").toUpperCase();
  const resource = cleanValue(resourceId, "affected identity");

  if (providerLabel === "Okta") {
    if (rule === "OKTA_PRIVILEGED_GROUP_EXPOSURE") {
      return `Okta Admin Console → Directory → Groups → ${resource}`;
    }
    if (rule.includes("ADMIN_EXPOSURE")) {
      return "Okta Admin Console → Security → Administrators";
    }
    return `Okta Admin Console → Directory → People → ${resource}`;
  }

  if (rule === "GW_2SV_DISABLED") {
    return `Google Admin Console → Directory → Users → ${resource} → Security → 2-Step Verification`;
  }
  if (rule === "GW_ADMIN_COUNT" || rule === "GW_SUPER_ADMIN_EXPOSURE") {
    return "Google Admin Console → Account → Admin roles";
  }

  return `Google Admin Console → Directory → Users → ${resource}`;
}

function getIdentityFixPlanContext({
  ruleId = "",
  fixPlanItem = {},
  enrichedFixPlanItem = {},
  visibleModalTask = {},
} = {}) {
  const raw = fixPlanItem?.raw || {};
  const enrichedRaw = enrichedFixPlanItem?.raw || {};
  const providerLabel = getIdentityProviderLabel(ruleId, fixPlanItem, enrichedFixPlanItem, visibleModalTask);
  const resourceId = cleanValue(
    enrichedFixPlanItem?.resource_id ||
      fixPlanItem?.resource_id ||
      visibleModalTask?.resource_id ||
      raw?.resource_id ||
      enrichedRaw?.resource_id,
    "affected identity"
  );
  const resourceType = cleanValue(
    enrichedFixPlanItem?.resource_type ||
      fixPlanItem?.resource_type ||
      visibleModalTask?.resource_type ||
      raw?.resource_type ||
      enrichedRaw?.resource_type,
    providerLabel === "Okta" ? "okta identity" : "google workspace identity"
  );
  const stream = cleanValue(
    enrichedFixPlanItem?.stream ||
      fixPlanItem?.stream ||
      visibleModalTask?.stream ||
      raw?.stream ||
      enrichedRaw?.stream,
    providerLabel === "Okta" ? "okta_identity_inventory" : "google_workspace_directory"
  );
  const lastCheckedAt =
    enrichedFixPlanItem?.last_checked_at ||
    fixPlanItem?.last_checked_at ||
    visibleModalTask?.last_checked_at ||
    raw?.last_seen_at ||
    enrichedRaw?.last_seen_at ||
    null;

  return {
    providerLabel,
    title: providerLabel === "Okta" ? "Affected Okta Identity Resource" : "Affected Google Workspace Identity Resource",
    badge: providerLabel === "Okta" ? "Okta Identity" : "Google Workspace Identity",
    copy: `OASYS identified the exact ${providerLabel} identity resource tied to this finding, so remediation can be performed in the source identity system and validated from refreshed inventory.`,
    path: getIdentityConsolePath(ruleId, providerLabel, resourceId),
    resourceId,
    resourceType,
    ruleLabel: getIdentityRuleLabel(ruleId),
    expectedAction: getIdentityExpectedAction(ruleId),
    stream,
    lastCheckedAt,
  };
}

function getIamResourceCardTitle({ isCredentialReport, isUnusedRole, isTrustPolicy, isPolicyEvent, isInlinePolicy }) {
  if (isCredentialReport) return "Affected IAM User";
  if (isUnusedRole) return "Affected IAM Role";
  if (isTrustPolicy) return "Affected IAM Trust Policy";
  if (isPolicyEvent || isInlinePolicy) return "Affected IAM Policy Change";
  return "Affected AWS Resource";
}

function getIamResourceCardCopy({ isCredentialReport, isUnusedRole, isTrustPolicy, isPolicyEvent, isInlinePolicy, fallbackCopy }) {
  if (isCredentialReport) {
    return "OASYS identified the exact IAM user and credential condition tied to this risk, so remediation can be completed without guessing.";
  }

  if (isUnusedRole) {
    return "OASYS identified the exact IAM role tied to this risk, so role usage can be reviewed before deletion, waiver, or remediation.";
  }

  if (isTrustPolicy) {
    return "OASYS identified the IAM role trust policy tied to this risk, so external principals and assume-role conditions can be reviewed before changes are made.";
  }

  if (isPolicyEvent || isInlinePolicy) {
    return "OASYS identified the IAM policy change tied to this risk, so attachment, actor, and permission scope can be reviewed before rollback or acceptance.";
  }

  return fallbackCopy;
}

function IamDecisionPanel({
  panel,
  onOpenApprovedException,
  approvedExceptionDisabled = false,
  approvedExceptionButtonLabel = "Start approved exception",
}) {
  if (!panel) return null;

  return (
    <div
      style={{
        border: "1px solid color-mix(in srgb, var(--warn), transparent 70%)",
        background: "color-mix(in srgb, var(--warn), transparent 94%)",
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "grid", gap: 4 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              color: "var(--warn)",
            }}
          >
            IAM Remediation Decision
          </div>
          <div style={{ color: "var(--text)", fontWeight: 900 }}>
            {panel.headline}
          </div>
          <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>
            {panel.detail}
          </div>
        </div>

        <span
          className="task-severity"
          style={{
            color: "var(--warn)",
            background: "color-mix(in srgb, var(--warn), transparent 90%)",
            border: "1px solid color-mix(in srgb, var(--warn), transparent 70%)",
            borderRadius: 999,
            padding: "4px 9px",
            fontSize: 11,
            fontWeight: 900,
            whiteSpace: "nowrap",
          }}
        >
          Review Before Change
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <MetaField label={panel.identityLabel || "IAM Identity"} value={panel.identityName} wrap />
        <MetaField label={panel.classificationLabel || "Identity Classification"} value={panel.identityType} wrap />
        <MetaField label={panel.conditionLabel || "Condition"} value={panel.condition} wrap />
        <MetaField label={panel.decisionLabel || "Safe Decision"} value={panel.decision} wrap />
      </div>

      {Array.isArray(panel.decisionPaths) && panel.decisionPaths.length > 0 ? (
        <div style={{ display: "grid", gap: 8 }}>
          <div
            style={{
              color: "var(--muted)",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: ".05em",
              textTransform: "uppercase",
            }}
          >
            Decision paths
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            {panel.decisionPaths.map((path, index) => {
              const color = getIamDecisionPathToneColor(path.tone);
              const isExceptionPath = path.tone === "warn";
              return (
                <div
                  key={`iam-decision-path-${index}`}
                  className="open-item-pill"
                  style={{
                    cursor: "default",
                    alignItems: "stretch",
                    display: "grid",
                    gap: 7,
                    borderColor: `color-mix(in srgb, ${color}, transparent 70%)`,
                    background: `color-mix(in srgb, ${color}, transparent 94%)`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span
                      style={{
                        color,
                        background: `color-mix(in srgb, ${color}, transparent 90%)`,
                        border: `1px solid color-mix(in srgb, ${color}, transparent 70%)`,
                        borderRadius: 999,
                        padding: "3px 8px",
                        fontSize: 11,
                        fontWeight: 900,
                        lineHeight: 1.1,
                      }}
                    >
                      {path.label}
                    </span>
                    <span style={{ color, fontSize: 11, fontWeight: 900 }}>Decision</span>
                  </div>
                  <div style={{ fontWeight: 950, fontSize: 13, color: "var(--text)" }}>{path.title}</div>
                  <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>{path.detail}</div>
                  {Array.isArray(path.evidence) && path.evidence.length > 0 ? (
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "1rem",
                        color: "var(--text)",
                        fontSize: 12,
                        lineHeight: 1.45,
                        display: "grid",
                        gap: 3,
                      }}
                    >
                      {path.evidence.map((item, evidenceIndex) => (
                        <li key={`iam-path-evidence-${index}-${evidenceIndex}`}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                  {isExceptionPath ? (
                    <div style={{ display: "flex", justifyContent: "flex-start", paddingTop: 2 }}>
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={onOpenApprovedException}
                        disabled={approvedExceptionDisabled}
                        title={
                          approvedExceptionDisabled
                            ? "Create or open a remediation task before approving an exception."
                            : "Open the approved exception governance form."
                        }
                      >
                        {approvedExceptionButtonLabel}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 6 }}>
        <div
          style={{
            color: "var(--muted)",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: ".05em",
            textTransform: "uppercase",
          }}
        >
          Safe remediation path
        </div>
        <ol
          style={{
            margin: 0,
            paddingLeft: "1.1rem",
            color: "var(--text)",
            fontSize: 12,
            lineHeight: 1.55,
            display: "grid",
            gap: 4,
          }}
        >
          {panel.recommendedPath.map((step, index) => (
            <li key={`iam-decision-${index}`}>{step}</li>
          ))}
        </ol>
      </div>
    </div>
  );
}


function dedupeDraftItems(items = []) {
  const seen = new Set();
  const output = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const cleaned = cleanValue(item, "");
    const key = normalizeText(cleaned);
    if (!cleaned || seen.has(key)) return;
    seen.add(key);
    output.push(cleaned);
  });

  return output;
}


const GENERIC_COPILOT_DRAFT_ITEM_PATTERNS = [
  "review the failing control or rule evidence",
  "open the governing sop",
  "assign an owner and document the remediation plan",
  "execute the remediation steps under change-control expectations",
  "collect evidence before and after remediation",
  "run oasys validation before closing the task",
  "failing evidence before remediation",
  "governing sop or procedure reference",
  "owner notes and remediation plan",
  "before/after configuration or process evidence",
  "run the relevant connector sync or assessment",
  "refresh posture and control status",
  "confirm the finding/control no longer fails",
  "attach validation evidence to the remediation task",
  "oasys validation result.",
  "do not close remediation work until validation evidence proves the control condition is resolved",
  "if no sop is linked, link or create the governing sop before treating the finding as procedurally controlled",
];

function isGenericCopilotDraftItem(item = "") {
  const value = normalizeText(cleanValue(item, ""));
  if (!value) return true;

  return GENERIC_COPILOT_DRAFT_ITEM_PATTERNS.some((pattern) => value === pattern || value.includes(pattern));
}

function keepRuleSpecificBackendDraftItems(items = []) {
  return dedupeDraftItems(items).filter((item) => !isGenericCopilotDraftItem(item));
}


const RULE_SPECIFIC_DRAFT_SECTION_LIMITS = {
  recommended_actions: 10,
  evidence_checklist: 8,
  validation_steps: 6,
  risk_notes: 4,
};

function getDraftItemTokens(item = "") {
  return normalizeText(cleanValue(item, ""))
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((token) => token.length > 3);
}

function draftItemsOverlap(left = "", right = "") {
  const a = normalizeText(cleanValue(left, ""));
  const b = normalizeText(cleanValue(right, ""));
  if (!a || !b) return false;
  if (a === b || a.includes(b) || b.includes(a)) return true;

  const aTokens = new Set(getDraftItemTokens(a));
  const bTokens = new Set(getDraftItemTokens(b));
  if (aTokens.size === 0 || bTokens.size === 0) return false;

  let overlap = 0;
  aTokens.forEach((token) => {
    if (bTokens.has(token)) overlap += 1;
  });

  const smallerSize = Math.min(aTokens.size, bTokens.size);
  return smallerSize >= 4 && overlap / smallerSize >= 0.72;
}

function pushDraftItemIfUnique(output, item) {
  const cleaned = cleanValue(item, "");
  if (!cleaned) return;

  const duplicate = output.some((existing) => draftItemsOverlap(existing, cleaned));
  if (!duplicate) output.push(cleaned);
}

function mergeRuleSpecificDraftSection(primaryItems = [], secondaryItems = [], limit = 8) {
  const output = [];
  const primary = dedupeDraftItems(primaryItems);
  const secondary = keepRuleSpecificBackendDraftItems(secondaryItems);

  primary.forEach((item) => {
    if (output.length < limit) pushDraftItemIfUnique(output, item);
  });

  secondary.forEach((item) => {
    if (output.length < limit) pushDraftItemIfUnique(output, item);
  });

  return output;
}

function getS3RemediationDraftModel({ ruleId = "", bucketName = "", settingLabel = "", expectedStateLabel = "" } = {}) {
  const rule = String(ruleId || "").toUpperCase();
  const bucket = cleanValue(bucketName, "the affected S3 bucket");
  const setting = cleanValue(settingLabel, getS3SettingLabel(rule));
  const expected = cleanValue(expectedStateLabel, getS3ExpectedStateLabel(rule));

  if (rule.includes("VERSIONING")) {
    return {
      pathway_id: "aws_s3_versioning_remediation",
      pathway_name: "S3 bucket versioning remediation",
      investigation_notes: `OASYS identified ${bucket} as failing ${rule}. The remediation objective is to enable Bucket Versioning and capture before/after evidence that the bucket now satisfies the expected state: ${expected}.`,
      recommended_actions: [
        `Open Amazon S3 and locate bucket ${bucket}.`,
        "Open the Properties tab and review Bucket Versioning.",
        "Enable Bucket Versioning if it is suspended or disabled.",
        "Confirm whether MFA delete or lifecycle rules are needed as a separate hardening decision.",
        "Save before/after screenshots or exported posture evidence showing Bucket Versioning is enabled.",
        "Return to OASYS and run Full Cloud Assessment to refresh AWS S3 posture and validate the task.",
      ],
      evidence_checklist: [
        `Before evidence showing ${bucket} versioning was not enabled or was suspended.`,
        `After evidence showing ${bucket} Bucket Versioning is enabled.`,
        "OASYS validation result showing the S3_VERSIONING finding cleared.",
        "Owner approval or change record if production storage settings were changed.",
      ],
      validation_steps: [
        "Run Full Cloud Assessment from the Fix Plan.",
        "Confirm AWS S3 posture sync completes successfully.",
        "Confirm normalized findings no longer contain a failing S3_VERSIONING row for this bucket.",
        "Close the remediation task after validation passes and evidence is attached.",
      ],
      risk_notes: [
        "Versioning improves recovery from overwrite/delete events but can increase storage costs if lifecycle rules are not managed.",
        "Do not delete historical object versions as part of this remediation unless a separate retention decision has been approved.",
      ],
    };
  }

  if (rule.includes("LOGGING")) {
    return {
      pathway_id: "aws_s3_logging_remediation",
      pathway_name: "S3 server access logging remediation",
      investigation_notes: `OASYS identified ${bucket} as failing ${rule}. The remediation objective is to configure server access logging or document an approved logging exception.`,
      recommended_actions: [
        `Open Amazon S3 and locate bucket ${bucket}.`,
        "Open Properties and review Server access logging.",
        "Select or create an approved log destination bucket with the required write permissions.",
        "Configure the target prefix so logs are retained in the expected location.",
        "Confirm lifecycle and retention expectations for the log destination bucket.",
        "Run Full Cloud Assessment in OASYS to validate the updated S3 posture.",
      ],
      evidence_checklist: [
        `Before evidence showing ${bucket} server access logging was not configured.`,
        "Destination bucket and prefix configuration evidence.",
        `After evidence showing ${bucket} server access logging is enabled.`,
        "OASYS validation result showing the S3_LOGGING finding cleared.",
      ],
      validation_steps: [
        "Run Full Cloud Assessment from the Fix Plan.",
        "Confirm AWS S3 posture sync records logging_target_bucket and logging_target_prefix.",
        "Confirm no failing S3_LOGGING normalized finding remains for this bucket.",
      ],
      risk_notes: [
        "Logging creates auditability for bucket access and supports incident investigation.",
        "Avoid logging loops by ensuring the destination bucket is configured correctly.",
      ],
    };
  }

  if (rule.includes("ENCRYPTION")) {
    return {
      pathway_id: "aws_s3_encryption_remediation",
      pathway_name: "S3 default encryption remediation",
      investigation_notes: `OASYS identified ${bucket} as failing ${rule}. The remediation objective is to enable default encryption and validate that new objects are protected according to policy.`,
      recommended_actions: [
        `Open Amazon S3 and locate bucket ${bucket}.`,
        "Open Properties and review Default encryption.",
        "Enable default encryption using the approved encryption option for this environment.",
        "Confirm KMS key ownership, access policy, and service dependencies if SSE-KMS is used.",
        "Save before/after encryption evidence.",
        "Run Full Cloud Assessment in OASYS to validate the updated S3 posture.",
      ],
      evidence_checklist: [
        `Before evidence showing ${bucket} default encryption was missing or non-compliant.`,
        "Encryption setting and KMS key evidence, if applicable.",
        `After evidence showing ${bucket} default encryption is enabled.`,
        "OASYS validation result showing the encryption finding cleared.",
      ],
      validation_steps: [
        "Run Full Cloud Assessment from the Fix Plan.",
        "Confirm AWS S3 posture sync captures encryption configuration.",
        "Confirm no failing S3 encryption normalized finding remains for this bucket.",
      ],
      risk_notes: [
        "Confirm applications have permission to use the selected KMS key before enforcing SSE-KMS.",
        "Existing objects may require a separate backfill or re-encryption project outside this task.",
      ],
    };
  }

  if (rule.includes("PUBLIC_ACCESS") || rule.includes("POLICY_PUBLIC")) {
    return {
      pathway_id: "aws_s3_public_access_remediation",
      pathway_name: "S3 public access remediation",
      investigation_notes: `OASYS identified ${bucket} as failing ${rule}. The remediation objective is to remove unintended public access or document a formally approved exception.`,
      recommended_actions: [
        `Open Amazon S3 and locate bucket ${bucket}.`,
        "Review Block Public Access, bucket policy, ACLs, and access points.",
        "Confirm whether any public access is intentionally required and approved.",
        "Enable Block Public Access or remove public principals from the bucket policy where appropriate.",
        "Validate application behavior after the policy change.",
        "Run Full Cloud Assessment in OASYS to confirm the public-access finding cleared.",
      ],
      evidence_checklist: [
        `Before evidence showing public access exposure for ${bucket}.`,
        "Bucket policy or Block Public Access before/after evidence.",
        "Owner approval for removal or exception decision.",
        "OASYS validation result showing the public-access finding cleared or exception documented.",
      ],
      validation_steps: [
        "Run Full Cloud Assessment from the Fix Plan.",
        "Confirm AWS S3 posture sync captures Block Public Access and bucket-policy state.",
        "Confirm no failing public-access normalized finding remains for this bucket unless waived by policy.",
      ],
      risk_notes: [
        "Public bucket changes can break static hosting or public distribution paths; confirm approved architecture before removal.",
        "Prefer CloudFront or scoped access patterns over direct public bucket access where possible.",
      ],
    };
  }

  return {
    pathway_id: "aws_s3_control_remediation",
    pathway_name: "S3 control remediation",
    investigation_notes: `OASYS identified ${bucket} as failing ${rule || "an S3 posture rule"}. Review ${setting} and bring the bucket to the expected state: ${expected}.`,
    recommended_actions: [
      `Open Amazon S3 and locate bucket ${bucket}.`,
      `Review ${setting}.`,
      `Update the bucket configuration to meet the expected state: ${expected}.`,
      "Save before/after evidence and run Full Cloud Assessment in OASYS.",
    ],
    evidence_checklist: [
      `Before evidence for ${bucket}.`,
      `After evidence showing ${setting} meets the expected state.`,
      "OASYS validation result.",
    ],
    validation_steps: [
      "Run Full Cloud Assessment from the Fix Plan.",
      "Confirm no failing normalized finding remains for this S3 rule/resource.",
    ],
    risk_notes: ["Confirm source-system changes are approved before closing the task."],
  };
}


function isGitHubConnectorDraftRule(ruleId = "", item = {}) {
  const rule = String(ruleId || item?.rule_id || item?.ruleId || "").toUpperCase();
  const source = normalizeText(
    item?.source ||
      item?.source_kind ||
      item?.sourceKind ||
      item?.stream ||
      item?.source_table ||
      item?.sourceTable ||
      item?.raw?.source ||
      item?.raw?.stream ||
      ""
  );

  return rule.startsWith("GH_") || source.includes("github");
}

function isCloudflareConnectorDraftRule(ruleId = "", item = {}) {
  const rule = String(ruleId || item?.rule_id || item?.ruleId || "").toUpperCase();
  const source = normalizeText(
    item?.source ||
      item?.source_kind ||
      item?.sourceKind ||
      item?.stream ||
      item?.source_table ||
      item?.sourceTable ||
      item?.raw?.source ||
      item?.raw?.stream ||
      ""
  );

  return rule.startsWith("CF_") || source.includes("cloudflare");
}

function getCodeEdgeCopilotDraftResourceLabel(item = {}, fallback = "affected resource") {
  return cleanValue(
    item?.resource_id ||
      item?.resourceId ||
      item?.repository ||
      item?.repo ||
      item?.zone_name ||
      item?.zoneName ||
      item?.raw?.resource_id ||
      item?.raw?.resourceId ||
      fallback,
    fallback
  );
}

function getGitHubCopilotRuleContext(ruleId = "", repository = "the affected repository") {
  const rule = String(ruleId || "").toUpperCase();
  const repo = cleanValue(repository, "the affected repository");

  if (rule === "GH_DEPENDABOT_DISABLED") {
    return {
      setting: "Dependabot alerts",
      sourcePath: `GitHub → ${repo} → Settings → Code security and analysis`,
      action: `Enable Dependabot alerts for ${repo}.`,
      evidenceTarget: "Dependabot alerts enabled",
      riskNote: "Dependabot alerts surface vulnerable dependencies; leaving them disabled reduces software supply-chain visibility.",
    };
  }

  if (rule === "GH_SECRET_SCANNING_DISABLED") {
    return {
      setting: "secret scanning",
      sourcePath: `GitHub → ${repo} → Settings → Code security and analysis`,
      action: `Enable secret scanning for ${repo} where the plan and repository type support it, or document why GitHub does not expose the setting.`,
      evidenceTarget: "secret scanning enabled or access-limited status documented",
      riskNote: "Secret scanning reduces the chance that credentials committed to code remain undetected.",
    };
  }

  if (rule === "GH_PUBLIC_REPOSITORY") {
    return {
      setting: "repository visibility",
      sourcePath: `GitHub → ${repo} → Settings → General → Danger Zone`,
      action: `Confirm ${repo} is private, or document an approved public-repository exception.`,
      evidenceTarget: "repository visibility approved",
      riskNote: "Public repositories can expose code, configuration, or operational patterns if not intentionally approved.",
    };
  }

  if (rule === "GH_BRANCH_PROTECTION_DISABLED") {
    return {
      setting: "branch protection",
      sourcePath: `GitHub → ${repo} → Settings → Branches → Branch protection rules`,
      action: `Enable branch protection for the default branch in ${repo}.`,
      evidenceTarget: "branch protection enabled",
      riskNote: "Branch protection reduces unauthorized or unreviewed changes to protected production branches.",
    };
  }

  if (rule === "GH_REQUIRED_REVIEWS_DISABLED") {
    return {
      setting: "required pull request reviews",
      sourcePath: `GitHub → ${repo} → Settings → Branches → Branch protection rules`,
      action: `Require pull request reviews before merging changes to the protected branch in ${repo}.`,
      evidenceTarget: "required reviews enabled",
      riskNote: "Required reviews create independent review evidence for code changes before merge.",
    };
  }

  if (rule === "GH_ADMIN_BYPASS_ENABLED") {
    return {
      setting: "admin bypass protection",
      sourcePath: `GitHub → ${repo} → Settings → Branches → Branch protection rules`,
      action: `Require protected branch rules to apply to administrators or document an approved exception for ${repo}.`,
      evidenceTarget: "administrator bypass restricted or approved",
      riskNote: "Administrator bypass can allow privileged users to merge changes without the expected controls.",
    };
  }

  return {
    setting: "GitHub repository security setting",
    sourcePath: `GitHub → ${repo}`,
    action: `Review and remediate the GitHub rule ${rule || "for this repository"}.`,
    evidenceTarget: "GitHub finding remediated",
    riskNote: "GitHub code-governance findings should be validated from refreshed connector evidence before closure.",
  };
}

function getCloudflareCopilotRuleContext(ruleId = "", zoneName = "the affected zone") {
  const rule = String(ruleId || "").toUpperCase();
  const zone = cleanValue(zoneName, "the affected zone");

  if (rule === "CF_WAF_DISABLED") {
    return {
      setting: "Cloudflare WAF",
      sourcePath: `Cloudflare → Websites → ${zone} → Security → WAF`,
      action: `Enable Cloudflare WAF for ${zone} and review the managed ruleset baseline.`,
      evidenceTarget: "WAF enabled",
      riskNote: "A disabled WAF reduces edge-layer protection for public-facing traffic.",
    };
  }

  if (rule === "CF_TLS_MIN_VERSION_WEAK") {
    return {
      setting: "minimum TLS version",
      sourcePath: `Cloudflare → Websites → ${zone} → SSL/TLS → Edge Certificates`,
      action: `Raise the minimum TLS version for ${zone} to TLS 1.2 or higher after confirming client compatibility.`,
      evidenceTarget: "minimum TLS 1.2 or higher",
      riskNote: "TLS 1.0/1.1 allow legacy protocol exposure and should not be retained without a documented exception.",
    };
  }

  if (rule === "CF_ALWAYS_USE_HTTPS_DISABLED") {
    return {
      setting: "Always Use HTTPS",
      sourcePath: `Cloudflare → Websites → ${zone} → SSL/TLS → Edge Certificates`,
      action: `Enable Always Use HTTPS for ${zone} and confirm HTTP requests redirect to HTTPS.`,
      evidenceTarget: "Always Use HTTPS enabled",
      riskNote: "Without HTTPS enforcement, users may reach the site over plaintext HTTP paths.",
    };
  }

  if (rule === "CF_SECURITY_LEVEL_LOW") {
    return {
      setting: "security level",
      sourcePath: `Cloudflare → Websites → ${zone} → Security → Settings`,
      action: `Set Cloudflare security level for ${zone} to medium or higher, or document an approved exception.`,
      evidenceTarget: "security level medium or higher",
      riskNote: "Low edge security level reduces Cloudflare challenge and screening sensitivity.",
    };
  }

  if (rule === "CF_DNSSEC_DISABLED") {
    return {
      setting: "DNSSEC",
      sourcePath: `Cloudflare → Websites → ${zone} → DNS → Settings → DNSSEC`,
      action: `Enable DNSSEC for ${zone}, complete registrar DS record configuration if required, or fix API access so DNSSEC status can be read.`,
      evidenceTarget: "DNSSEC enabled or access-limited state resolved",
      riskNote: "DNSSEC protects DNS integrity; unknown status should be resolved with better API access or explicit configuration evidence.",
    };
  }

  if (rule === "CF_ZONE_ACTIVITY_OBSERVED") {
    return {
      setting: "Cloudflare zone inventory",
      sourcePath: `Cloudflare → Websites → ${zone}`,
      action: `Continue scheduled Cloudflare connector syncs for ${zone} and monitor evidence freshness.`,
      evidenceTarget: "fresh Cloudflare zone inventory evidence",
      riskNote: "This is a positive evidence signal; maintain connector freshness rather than remediating a failed setting.",
    };
  }

  return {
    setting: "Cloudflare edge security setting",
    sourcePath: `Cloudflare → Websites → ${zone}`,
    action: `Review and remediate the Cloudflare rule ${rule || "for this zone"}.`,
    evidenceTarget: "Cloudflare finding remediated",
    riskNote: "Cloudflare edge findings should be validated from refreshed connector evidence before closure.",
  };
}

function getCodeEdgeCopilotDraftModel({ ruleId = "", item = {}, resourceId = "", fixPlanSteps = [], fixPlanValidation = "" } = {}) {
  const rule = String(ruleId || "").toUpperCase();
  const isGithub = isGitHubConnectorDraftRule(rule, item);
  const isCloudflare = isCloudflareConnectorDraftRule(rule, item);
  if (!isGithub && !isCloudflare) return null;

  const resource = getCodeEdgeCopilotDraftResourceLabel(
    item,
    isGithub ? "the affected GitHub repository" : "the affected Cloudflare zone"
  );
  const context = isGithub
    ? getGitHubCopilotRuleContext(rule, resource)
    : getCloudflareCopilotRuleContext(rule, resource);
  const sourceLabel = isGithub ? "GitHub" : "Cloudflare";
  const syncLabel = isGithub ? "GitHub repository sync" : "Cloudflare zone sync";
  const normalizePayload = isGithub
    ? "source=github, stream=github_repos"
    : "source=cloudflare, stream=cloudflare_default";
  const family = isGithub ? "github_code_governance" : "cloudflare_edge_security";

  return {
    pathway_id: `${family}_remediation`,
    pathway_name: isGithub ? "GitHub code governance remediation" : "Cloudflare edge security remediation",
    investigation_notes: `OASYS identified ${rule} on ${resource}. Review ${context.setting} in ${sourceLabel}, apply the source-system fix, and validate from refreshed connector evidence. Required action: ${context.action}`,
    recommended_actions: dedupeDraftItems([
      `Open ${sourceLabel} and navigate to ${context.sourcePath}.`,
      context.action,
      `Confirm the affected resource is ${resource}.`,
      "Coordinate the change with the resource owner before modifying production settings.",
      `Capture before/after evidence showing ${context.evidenceTarget}.`,
      `Rerun the ${syncLabel}.`,
      `Rerun normalization (${normalizePayload}) and refresh posture/freshness.`,
    ]),
    evidence_checklist: dedupeDraftItems([
      `Failing OASYS normalized finding for ${rule} before remediation.`,
      `Before screenshot or exported setting evidence from ${context.sourcePath}.`,
      `After screenshot or exported setting evidence showing ${context.evidenceTarget}.`,
      `${syncLabel} result after remediation.`,
      `Normalized finding output showing ${rule} is pass, cleared, or no longer failed.`,
      "Posture/freshness refresh result showing the affected SOC2 control updated.",
      "Owner approval or change-control note for the source-system change.",
    ]),
    validation_steps: dedupeDraftItems([
      `Complete the ${sourceLabel} source-system change for ${resource}.`,
      `Rerun the ${syncLabel}.`,
      `Rerun OASYS normalization for ${normalizePayload}.`,
      "Refresh posture/freshness after normalization completes.",
      fixPlanValidation || `Validation passes when refreshed ${sourceLabel} connector evidence no longer reports ${rule} as failed for ${resource}.`,
      "Attach validation evidence before closing the remediation task.",
    ]),
    risk_notes: dedupeDraftItems([
      context.riskNote,
      "Do not close this remediation task until refreshed connector evidence proves the source condition is resolved or an approved exception is documented.",
      "If the API cannot read the setting, update connector permissions or attach approved manual evidence and document the access-limited condition.",
    ]),
    draft_metadata: {
      rule_family: family,
      source_provider: sourceLabel.toLowerCase(),
      source_path: context.sourcePath,
      required_action: context.action,
      evidence_target: context.evidenceTarget,
      source: "fix_plan_code_edge_rule_specific_enrichment",
    },
  };
}

function getIamCopilotDraftModel({ ruleId = "", awsContext = {}, iamDecisionPanel = null, fixPlanSteps = [] } = {}) {
  const rule = String(ruleId || "").toUpperCase();
  const decision = iamDecisionPanel || getIamDecisionPanelData({ ruleId: rule, awsContext });
  if (!decision) return null;

  const identity = cleanValue(decision.identityName, getIamIdentityTarget(awsContext));
  const family = isIamUnusedRoleRule(rule)
    ? "role_usage"
    : isIamTrustPolicyRule(rule)
    ? "trust_policy"
    : isIamPolicyEventRule(rule)
    ? "policy_change"
    : "credential";

  const pathwayByFamily = {
    credential: ["aws_iam_credential_remediation", "IAM credential remediation"],
    role_usage: ["aws_iam_role_usage_remediation", "IAM role usage remediation"],
    trust_policy: ["aws_iam_trust_policy_remediation", "IAM trust policy remediation"],
    policy_change: ["aws_iam_policy_change_remediation", "IAM policy change remediation"],
  };

  const [pathway_id, pathway_name] = pathwayByFamily[family] || pathwayByFamily.credential;
  const safePath = Array.isArray(decision.recommendedPath) ? decision.recommendedPath : [];
  const exactSteps = Array.isArray(fixPlanSteps) ? fixPlanSteps : [];

  const investigation_notes = `${decision.headline} ${decision.detail} Affected identity: ${identity}. Rule: ${rule}. Safe decision: ${decision.decision}`;

  const commonEvidence = [
    "OASYS Fix Plan context and failing normalized finding before remediation.",
    "Owner approval and remediation decision notes.",
    "Before/after AWS evidence for the affected IAM identity.",
    "OASYS validation result after Full Cloud Assessment.",
  ];

  const evidenceByFamily = {
    credential: [
      `IAM credential report evidence for ${identity}.`,
      "Access key inventory, last-used data, rotation evidence, or MFA assignment evidence as applicable.",
      "Workload dependency, secret location, and rollback notes before any key is disabled.",
    ],
    role_usage: [
      `IAM role summary and policy attachment evidence for ${identity}.`,
      "IAM Access Advisor service-last-accessed evidence.",
      "CloudTrail AssumeRole activity review or no-recent-use evidence.",
      "Deletion evidence or approved exception documentation.",
    ],
    trust_policy: [
      `Trust policy before/after evidence for ${identity}.`,
      "Approved external principal, ExternalId, SourceAccount, SourceArn, organization, or MFA condition evidence as applicable.",
      "CloudTrail AssumeRole evidence for external principal usage.",
    ],
    policy_change: [
      `Policy attachment or inline policy evidence for ${identity}.`,
      "CloudTrail actor/event evidence for the policy change.",
      "Least-privilege review and approval or rollback evidence.",
    ],
  };

  const riskByFamily = {
    credential: [
      "Do not deactivate active access keys until workload ownership, secret location, dependency health, and rollback plan are confirmed.",
      "Service-style IAM users should be migrated to roles or scoped service integrations where possible.",
    ],
    role_usage: [
      "Unused-role findings can be false positives for low-frequency jobs; verify Access Advisor and CloudTrail before deletion.",
      "Do not delete service-linked or deployment roles without confirming dependencies and rollback.",
    ],
    trust_policy: [
      "Changing trust policies can break vendors, cross-account workflows, deployment pipelines, and automation.",
      "External trusts should be restricted with approved principals and conditions, not blindly removed without owner review.",
    ],
    policy_change: [
      "Detaching policies can break production workloads; confirm effective permissions and approval path first.",
      "If access is approved, document the owner, purpose, and least-privilege review instead of treating it as an unauthorized change.",
    ],
  };

  return {
    pathway_id,
    pathway_name,
    investigation_notes,
    recommended_actions: dedupeDraftItems([...safePath, ...exactSteps]),
    evidence_checklist: dedupeDraftItems([...(evidenceByFamily[family] || []), ...commonEvidence]),
    validation_steps: [
      "Complete the source-system remediation or approved exception documentation.",
      "Move the remediation task to Ready for Validation.",
      "Run Full Cloud Assessment from OASYS.",
      "Confirm the IAM sync, normalization, posture refresh, and task validation complete successfully.",
      "Close the task only after validation passes or the approved exception path is documented.",
    ],
    risk_notes: dedupeDraftItems(riskByFamily[family] || []),
    draft_metadata: {
      rule_family: `iam_${family}`,
      identity,
      safe_decision: decision.decision,
      decision_paths: Array.isArray(decision.decisionPaths)
        ? decision.decisionPaths.map((path) => ({
            label: path.label,
            title: path.title,
            detail: path.detail,
            evidence: path.evidence || [],
            tone: path.tone || "default",
          }))
        : [],
      source: "fix_plan_rule_specific_enrichment",
    },
  };
}

function getDraftSopTitle(sop = {}) {
  return (
    sop?.title ||
    sop?.sop_title ||
    sop?.name ||
    sop?.code ||
    "No linked governing SOP"
  );
}

function getEc2CopilotDraftModel({ ruleId = "", resourceId = "", fixPlanSteps = [], fixPlanValidation = "" } = {}) {
  const rule = String(ruleId || "").toUpperCase();
  if (rule !== "EC2_EBS_ENCRYPTION_ENABLED" && rule !== "EC2_IMDSV2_REQUIRED") return null;
  const resource = cleanValue(resourceId, "the affected EC2 resource");
  const ebs = rule === "EC2_EBS_ENCRYPTION_ENABLED";
  const actions = Array.isArray(fixPlanSteps) ? fixPlanSteps.filter(Boolean) : [];
  // The Fix Plan is the authoritative rule-specific source; do not replace it with generic Copilot steps.
  if (!actions.length || !fixPlanValidation) return null;
  return {
    title: ebs ? `Encrypt EBS volume ${resource}` : `Require IMDSv2 on EC2 instance ${resource}`,
    pathway_id: ebs ? "aws_ec2_ebs_encryption_remediation" : "aws_ec2_imdsv2_enforcement_remediation",
    pathway_name: ebs ? "EC2 EBS encryption remediation" : "EC2 IMDSv2 enforcement remediation",
    investigation_notes: ebs
      ? `Verified EC2 finding ${rule} affects volume ${resource}. An existing unencrypted EBS volume requires a controlled encrypted replacement; obtain approval and preserve recovery options.`
      : `Verified EC2 finding ${rule} affects instance ${resource}. Test IMDSv2 compatibility before enforcing HttpTokens=required to avoid workload disruption.`,
    recommended_actions: actions,
    evidence_checklist: ebs ? [
      `Before evidence for EBS volume ${resource}: account, region, AZ, attachment and encryption state.`,
      "Approved change, governing SOP reference, workload-owner signoff and rollback plan.",
      "Application-consistent snapshot, encrypted copy and replacement-volume identifiers and KMS key details.",
      "After DescribeVolumes evidence, attachment mapping, data integrity and application smoke-test results.",
      "Published verified OASYS EC2 capture and independent finding-clearance evidence.",
    ] : [
      `Before DescribeInstances MetadataOptions evidence for ${resource} (HttpTokens, HttpEndpoint, hop limit).`,
      "Owner approval and IMDSv2 compatibility tests for SDKs, containers, startup scripts and agents.",
      "After MetadataOptions evidence proving HttpTokens=required and application health results.",
      "Launch template/default change evidence where applicable and authorized rollback record.",
      "Published verified OASYS EC2 capture and independent finding-clearance evidence.",
    ],
    validation_steps: [
      "Complete approved source-system changes without bypassing the governing SOP and change controls.",
      "Run the existing OASYS evidence capture and wait for verified EC2 publication.",
      fixPlanValidation,
      "Attach independent validation evidence to the tracked remediation task before closure.",
    ],
    risk_notes: ebs ? [
      "An unencrypted EBS volume cannot be encrypted in place; replacement may require a maintenance window and downtime.",
      "Verify KMS grants, application consistency, device mappings and rollback before detaching the original volume.",
    ] : [
      "Enforcing IMDSv2 can break legacy SDKs, agents or container metadata access; test compatibility first.",
      "Retain an approved rollback and verify application startup and credential retrieval after enforcement.",
    ],
    draft_metadata: { rule_family: ebs ? "ec2_ebs_encryption" : "ec2_imdsv2", source_provider: "aws_ec2", source: "verified_ec2_fix_plan_enrichment" },
  };
}

function mergeCopilotDraftWithRuleSpecificContext({
  baseDraft = {},
  fixPlanItem = {},
  enrichedFixPlanItem = {},
  fixPlanAwsContext = {},
  fixPlanSteps = [],
  fixPlanValidation = "",
  fixPlanRelatedSops = [],
  relatedSopRemediation = {},
  visibleModalTask = {},
  isCredentialReport = false,
  isAccessKey = false,
  isActiveAccessKey = false,
  isInlinePolicy = false,
} = {}) {
  const draft = baseDraft && typeof baseDraft === "object" ? baseDraft : {};
  const ruleId = cleanValue(fixPlanItem?.rule_id || enrichedFixPlanItem?.rule_id, "");
  const controlId = cleanValue(fixPlanItem?.control_id || enrichedFixPlanItem?.control_id, "");
  const frameworkValue = cleanValue(fixPlanItem?.framework || enrichedFixPlanItem?.framework, "");
  const resourceType = cleanValue(
    fixPlanItem?.resource_type || enrichedFixPlanItem?.resource_type || visibleModalTask?.resource_type,
    ""
  );
  const resourceId = cleanValue(
    fixPlanItem?.resource_id || enrichedFixPlanItem?.resource_id || visibleModalTask?.resource_id || fixPlanAwsContext?.resourceId,
    ""
  );
  const label = buildControlLabel(controlId, ruleId);
  const linkedSop = fixPlanRelatedSops?.[0] || null;
  const linkedSopTitle = linkedSop ? getDraftSopTitle(linkedSop) : "No linked governing SOP";
  const taskStatus = cleanValue(visibleModalTask?.status, "not_created");
  const validationStatus = cleanValue(visibleModalTask?.validation_status, "pending");

  const ec2Model = getEc2CopilotDraftModel({ ruleId, resourceId, fixPlanSteps, fixPlanValidation });

  const s3Model = !ec2Model && isS3PostureRule(ruleId, resourceType)
    ? getS3RemediationDraftModel({
        ruleId,
        bucketName: resourceId,
        settingLabel: getS3SettingLabel(ruleId),
        expectedStateLabel: getS3ExpectedStateLabel(ruleId),
      })
    : null;

  const iamDecisionPanel = getIamDecisionPanelData({
    ruleId,
    awsContext: fixPlanAwsContext,
    isCredentialReport,
    isAccessKey,
    isActiveAccessKey,
    isInlinePolicy,
  });

  const codeEdgeModel = !ec2Model && !s3Model
    ? getCodeEdgeCopilotDraftModel({
        ruleId,
        item: enrichedFixPlanItem || fixPlanItem,
        resourceId,
        fixPlanSteps,
        fixPlanValidation,
      })
    : null;

  const iamModel = !ec2Model && !s3Model && !codeEdgeModel
    ? getIamCopilotDraftModel({
        ruleId,
        awsContext: fixPlanAwsContext,
        iamDecisionPanel,
        fixPlanSteps,
      })
    : null;

  const model = ec2Model || s3Model || codeEdgeModel || iamModel || null;
  if (!model) return draft;

  const title = model.title || draft.title || `Remediate ${label}`;
  const investigationNotes = model.investigation_notes || draft.investigation_notes || draft.finding_summary || "Review the failing evidence and complete remediation under change-control expectations.";

  return {
    ...draft,
    title,
    investigation_notes: investigationNotes,
    finding_summary: draft.finding_summary || fixPlanItem?.reason_summary || enrichedFixPlanItem?.reason_summary || investigationNotes,
    pathway_id: model.pathway_id || draft.pathway_id || "fix_plan_rule_specific_remediation",
    pathway_name: model.pathway_name || draft.pathway_name || "Rule-specific remediation plan",
    linked_sop_id: ec2Model
      ? getPrimarySopId(fixPlanRelatedSops, relatedSopRemediation) || null
      : draft.linked_sop_id || getPrimarySopId(fixPlanRelatedSops, relatedSopRemediation),
    recommended_actions: mergeRuleSpecificDraftSection(
      model.recommended_actions || [],
      draft.recommended_actions || [],
      RULE_SPECIFIC_DRAFT_SECTION_LIMITS.recommended_actions
    ),
    evidence_checklist: mergeRuleSpecificDraftSection(
      model.evidence_checklist || [],
      draft.evidence_checklist || [],
      RULE_SPECIFIC_DRAFT_SECTION_LIMITS.evidence_checklist
    ),
    validation_steps: mergeRuleSpecificDraftSection(
      [
        ...(model.validation_steps || []),
        ...keepRuleSpecificBackendDraftItems(fixPlanValidation ? [fixPlanValidation] : []),
      ],
      draft.validation_steps || [],
      RULE_SPECIFIC_DRAFT_SECTION_LIMITS.validation_steps
    ),
    risk_notes: mergeRuleSpecificDraftSection(
      model.risk_notes || [],
      draft.risk_notes || [],
      RULE_SPECIFIC_DRAFT_SECTION_LIMITS.risk_notes
    ),
    draft_metadata: {
      ...(draft.draft_metadata || {}),
      ...(model.draft_metadata || {}),
      framework: frameworkValue,
      control_id: controlId,
      rule_id: ruleId,
      resource_id: resourceId,
      task_status: taskStatus,
      validation_status: validationStatus,
      linked_sop_title: linkedSopTitle,
      rule_specific_enriched: true,
    },
  };
}

function RulePlanDiagnosticPill({ diagnostic }) {
  if (!diagnostic) return null;

  const label = diagnostic.label || "Rule plan coverage";
  const family = diagnostic.family || "generic";
  const tone = getRulePlanTone(diagnostic);
  const color = getRulePlanColor(tone);
  const title = diagnostic.is_generic_fallback
    ? "This rule is using the standard remediation plan. This is valid baseline guidance and can be upgraded to a dedicated rule-family plan later."
    : `This rule is using the ${family} rule-plan family.`;

  return (
    <span
      className="task-severity"
      title={title}
      style={{
        color,
        background: `color-mix(in srgb, ${color}, transparent 91%)`,
        border: `1px solid color-mix(in srgb, ${color}, transparent 72%)`,
        borderRadius: 999,
        padding: "4px 9px",
        fontSize: 11,
        fontWeight: 900,
        width: "fit-content",
        whiteSpace: "nowrap",
      }}
    >
      Rule plan: {label}
    </span>
  );
}

function hasMeaningfulSourceContext(value = {}) {
  if (!value || typeof value !== "object") return false;

  return Object.values(value).some((item) => {
    if (Array.isArray(item)) return item.length > 0;
    if (item && typeof item === "object") return Object.keys(item).length > 0;
    const cleaned = cleanValue(item, "");
    return Boolean(cleaned && cleaned !== "—");
  });
}

function getCodeEdgeRequiredAction(ruleId = "", providerLabel = "") {
  const rule = String(ruleId || "").toUpperCase();

  if (rule === "CF_WAF_DISABLED") {
    return "Enable Cloudflare WAF and review the managed ruleset baseline.";
  }
  if (rule === "CF_TLS_MIN_VERSION_WEAK") {
    return "Raise the Cloudflare minimum TLS version to 1.2 or higher.";
  }
  if (rule === "CF_ALWAYS_USE_HTTPS_DISABLED") {
    return "Enable Always Use HTTPS and confirm HTTP requests redirect to HTTPS.";
  }
  if (rule === "CF_SECURITY_LEVEL_LOW") {
    return "Set Cloudflare security level to medium or higher unless an approved exception exists.";
  }
  if (rule === "CF_DNSSEC_DISABLED") {
    return "Enable DNSSEC or update token permissions so DNSSEC status can be verified.";
  }
  if (rule === "CF_ZONE_ACTIVITY_OBSERVED") {
    return "Continue scheduled Cloudflare sync and evidence freshness monitoring.";
  }

  if (rule === "GH_DEPENDABOT_DISABLED") {
    return "Enable Dependabot alerts for the repository.";
  }
  if (rule === "GH_SECRET_SCANNING_DISABLED") {
    return "Enable secret scanning or verify repository security settings access.";
  }
  if (rule === "GH_PUBLIC_REPOSITORY") {
    return "Confirm the repository is private or document approved public exposure.";
  }
  if (rule === "GH_BRANCH_PROTECTION_DISABLED") {
    return "Enable branch protection on the default branch.";
  }
  if (rule === "GH_REQUIRED_REVIEWS_DISABLED") {
    return "Require pull request reviews before merge.";
  }
  if (rule === "GH_ADMIN_BYPASS_ENABLED") {
    return "Disable admin bypass or document an approved exception for protected branch enforcement.";
  }

  return `Review the ${providerLabel || "code/edge"} finding and remediate the affected source-system setting.`;
}

function getCodeEdgeSourcePath(ruleId = "", providerLabel = "", resourceId = "") {
  const rule = String(ruleId || "").toUpperCase();
  const resource = cleanValue(resourceId, providerLabel === "GitHub" ? "repository" : "zone");

  if (providerLabel === "Cloudflare") {
    if (rule === "CF_WAF_DISABLED") return `Cloudflare → Websites → ${resource} → Security → WAF`;
    if (rule === "CF_TLS_MIN_VERSION_WEAK") return `Cloudflare → Websites → ${resource} → SSL/TLS → Edge Certificates`;
    if (rule === "CF_ALWAYS_USE_HTTPS_DISABLED") return `Cloudflare → Websites → ${resource} → SSL/TLS → Edge Certificates`;
    if (rule === "CF_DNSSEC_DISABLED") return `Cloudflare → Websites → ${resource} → DNS → Settings → DNSSEC`;
    if (rule === "CF_SECURITY_LEVEL_LOW") return `Cloudflare → Websites → ${resource} → Security → Settings`;
    return `Cloudflare → Websites → ${resource}`;
  }

  if (providerLabel === "GitHub") {
    if (rule === "GH_DEPENDABOT_DISABLED" || rule === "GH_SECRET_SCANNING_DISABLED") {
      return `GitHub → ${resource} → Settings → Code security and analysis`;
    }
    if (
      rule === "GH_BRANCH_PROTECTION_DISABLED" ||
      rule === "GH_REQUIRED_REVIEWS_DISABLED" ||
      rule === "GH_ADMIN_BYPASS_ENABLED"
    ) {
      return `GitHub → ${resource} → Settings → Branches → Branch protection rules`;
    }
    if (rule === "GH_PUBLIC_REPOSITORY") return `GitHub → ${resource} → Settings → General → Danger Zone`;
    return `GitHub → ${resource} → Settings`;
  }

  return "Source system → affected setting";
}

function getCodeEdgeResourceType(ruleId = "", providerLabel = "") {
  const rule = String(ruleId || "").toUpperCase();

  if (providerLabel === "Cloudflare") {
    if (rule.includes("TLS") || rule.includes("HTTPS")) return "Cloudflare TLS setting";
    if (rule.includes("WAF")) return "Cloudflare WAF setting";
    if (rule.includes("DNSSEC")) return "Cloudflare DNSSEC setting";
    return "Cloudflare zone";
  }

  if (providerLabel === "GitHub") {
    if (rule.includes("BRANCH") || rule.includes("REVIEWS") || rule.includes("BYPASS")) return "GitHub branch protection";
    if (rule.includes("DEPENDABOT")) return "GitHub Dependabot setting";
    if (rule.includes("SECRET")) return "GitHub secret scanning setting";
    return "GitHub repository";
  }

  return "Code/edge setting";
}


export default function FixPlanModal({
  fixPlanItem,
  framework,
  apiBaseUrl,
  getAccessToken,
  tenantId,
  visibleModalTask,
  fixPlanAwsContext,
  fixPlanAwsResourceCopy,
  fixPlanIdentityLabel,
  fixPlanPolicyLabel,
  fixPlanPolicyValue,
  fixPlanEventLabel,
  fixPlanEventValue,
  fixPlanPassedEvidenceText,
  isFixPlanCredentialReport,
  isFixPlanAccessKey,
  isFixPlanActiveAccessKey,
  isFixPlanInlinePolicy,
  enrichedFixPlanItem,
  fixPlanSteps,
  fixPlanRelatedSops,
  fixPlanValidation,
  fixPlanRulePlanDiagnostic,
  remediationTaskNotice,
  remediationTaskError,
  remediationTaskCreating,
  remediationTaskUpdating,
  relatedSopRemediation,
  fullAssessmentRunning,
  userId,
  userName,
  handleCloseFixPlan,
  handleCreateRemediationTask,
  handleUpdateRemediationTask,
  onRemediationTaskUpdated,
  handleMarkTaskReadyForValidation,
  handleRunFullCloudAssessment,
  setMode,
  getRelatedSopTitle,
  getRelatedSopVersionLabel,
  getRelatedSopReleaseLabel,
}) {
  const [remediationDraft, setRemediationDraft] = useState(null);
  const [remediationDraftLoading, setRemediationDraftLoading] = useState(false);
  const [remediationDraftError, setRemediationDraftError] = useState("");
  const [remediationDraftNotice, setRemediationDraftNotice] = useState("");
  const [remediationDraftApplying, setRemediationDraftApplying] = useState(false);
  const [remediationDraftApplied, setRemediationDraftApplied] = useState(false);
  const [approvedExceptionOpen, setApprovedExceptionOpen] = useState(false);
  const [approvedExceptionSubmitting, setApprovedExceptionSubmitting] = useState(false);
  const [approvedExceptionError, setApprovedExceptionError] = useState("");
  const [approvedExceptionNotice, setApprovedExceptionNotice] = useState("");
  const [approvedExceptionEvidenceCount, setApprovedExceptionEvidenceCount] = useState(() =>
    getTaskEvidenceCount(visibleModalTask)
  );
  const [approvedExceptionEvidenceLoading, setApprovedExceptionEvidenceLoading] = useState(false);
  const [approvedExceptionForm, setApprovedExceptionForm] = useState(() =>
    buildDefaultApprovedExceptionForm()
  );
  const approvedExceptionFormRef = useRef(null);
  const approvedExceptionRecordedRef = useRef(null);

  const {
    setCopilotContext,
    clearCopilotContext,
    registerCopilotWorkflowAction,
  } = useAdvisor();

  const effectiveApiBaseUrl = useMemo(() => getApiBaseUrl(apiBaseUrl), [apiBaseUrl]);

  const primarySopId = useMemo(
    () => getPrimarySopId(fixPlanRelatedSops, relatedSopRemediation),
    [fixPlanRelatedSops, relatedSopRemediation]
  );

  const effectiveTenantId = useMemo(
    () =>
      cleanValue(tenantId, "") ||
      cleanValue(fixPlanItem?.tenant_id, "") ||
      cleanValue(fixPlanItem?.tenantId, "") ||
      cleanValue(enrichedFixPlanItem?.tenant_id, "") ||
      cleanValue(enrichedFixPlanItem?.tenantId, "") ||
      cleanValue(process.env.REACT_APP_TENANT_ID, ""),
    [tenantId, fixPlanItem, enrichedFixPlanItem]
  );


  const visibleModalTaskStatus = normalizeText(visibleModalTask?.status || "");

  const approvedExceptionRecorded = isApprovedExceptionTask(visibleModalTask);
  const approvedExceptionLastValidation = getTaskLastValidation(visibleModalTask) || {};
  const approvedExceptionMetadata = approvedExceptionLastValidation?.metadata || {};
  const approvedExceptionDecisionPath = cleanValue(
    approvedExceptionMetadata?.decision_path || getApprovedExceptionDecisionPath(fixPlanItem?.rule_id),
    "approved_exception"
  );
  const approvedExceptionDecisionOwner = cleanValue(
    approvedExceptionMetadata?.business_owner || visibleModalTask?.owner_name || userName,
    "—"
  );
  const approvedExceptionReviewDate = cleanValue(approvedExceptionMetadata?.review_date, "—");
  const approvedExceptionEvidenceRef = cleanValue(
    approvedExceptionLastValidation?.evidence_ref || approvedExceptionForm?.evidence_ref,
    "—"
  );
  const approvedExceptionKnownEvidenceCount =
    approvedExceptionEvidenceCount !== null && approvedExceptionEvidenceCount !== undefined
      ? approvedExceptionEvidenceCount
      : getTaskEvidenceCount(visibleModalTask);
  const approvedExceptionHasEvidence =
    Number.isFinite(Number(approvedExceptionKnownEvidenceCount)) &&
    Number(approvedExceptionKnownEvidenceCount) > 0;

  const fixPlanDraftResetKey = useMemo(
    () =>
      [
        cleanValue(fixPlanItem?.framework || framework, ""),
        cleanValue(fixPlanItem?.control_id, ""),
        cleanValue(fixPlanItem?.rule_id, ""),
        cleanValue(fixPlanItem?.resource_id, ""),
        getTaskId(visibleModalTask),
      ]
        .map((value) => normalizeText(value))
        .join("::"),
    [
      fixPlanItem?.framework,
      fixPlanItem?.control_id,
      fixPlanItem?.rule_id,
      fixPlanItem?.resource_id,
      framework,
      visibleModalTask,
    ]
  );

  useEffect(() => {
    setRemediationDraft(null);
    setRemediationDraftError("");
    setRemediationDraftNotice("");
    setRemediationDraftApplied(false);
    setRemediationDraftLoading(false);
    setRemediationDraftApplying(false);
    setApprovedExceptionOpen(false);
    setApprovedExceptionError("");
    setApprovedExceptionNotice("");
    setApprovedExceptionSubmitting(false);
    setApprovedExceptionEvidenceCount(getTaskEvidenceCount(visibleModalTask));
    setApprovedExceptionEvidenceLoading(false);
  }, [fixPlanDraftResetKey, visibleModalTask]);

  const appliedCopilotDraft = useMemo(
    () => getAppliedCopilotDraftFromTask(visibleModalTask),
    [visibleModalTask]
  );

  const effectiveRemediationDraft = remediationDraft || appliedCopilotDraft;
  const isViewingAppliedCopilotDraft = !remediationDraft && Boolean(appliedCopilotDraft);
  const hasAppliedCopilotDraft = Boolean(appliedCopilotDraft);
  const effectiveDraftApplied = remediationDraftApplied || isViewingAppliedCopilotDraft;

  const copilotNextStepGuidance = useMemo(() => {
    const ruleId = String(fixPlanItem?.rule_id || fixPlanItem?.ruleId || "").toUpperCase();
    const isGitHubDraft = isGitHubConnectorDraftRule(ruleId, fixPlanItem);
    const isCloudflareDraft = isCloudflareConnectorDraftRule(ruleId, fixPlanItem);
    const isCodeEdgeDraft = isGitHubDraft || isCloudflareDraft;
    const codeEdgeResource = getCodeEdgeCopilotDraftResourceLabel(
      fixPlanItem,
      isGitHubDraft ? "the affected GitHub repository" : "the affected Cloudflare zone"
    );
    const codeEdgeContext = isGitHubDraft
      ? getGitHubCopilotRuleContext(ruleId, codeEdgeResource)
      : isCloudflareDraft
      ? getCloudflareCopilotRuleContext(ruleId, codeEdgeResource)
      : null;
    const codeEdgeSourceLabel = isGitHubDraft ? "GitHub" : isCloudflareDraft ? "Cloudflare" : "source system";
    const codeEdgeSyncLabel = isGitHubDraft ? "GitHub repository sync" : "Cloudflare zone sync";
    const codeEdgeNormalizePayload = isGitHubDraft
      ? "source=github, stream=github_repos"
      : "source=cloudflare, stream=cloudflare_default";

    const roleTarget =
      cleanValue(fixPlanAwsContext?.roleName, "") ||
      cleanValue(fixPlanAwsContext?.identityName, "") ||
      cleanValue(fixPlanAwsContext?.resourceId, "") ||
      cleanValue(fixPlanItem?.resource_id, "") ||
      "the affected IAM role";

    if (!visibleModalTask) {
      return {
        tone: "accent",
        title: "Next step: create the tracked task.",
        detail:
          "This draft is review-only. Create a remediation task first, then apply the draft to that task.",
        steps: [],
      };
    }

    if (!effectiveDraftApplied) {
      return {
        tone: "accent",
        title: "Task is ready for this draft.",
        detail:
          "Review the Copilot plan, then apply it to the tracked remediation task when ready.",
        steps: [],
      };
    }

    if (visibleModalTaskStatus === "open") {
      return {
        tone: "ok",
        title: "Copilot draft applied to tracked task.",
        detail: "Click Start Work to begin the source-system remediation.",
        steps: [
          "Click Start Work below.",
          "Use the Copilot recommended actions as the working checklist.",
          "Do not mark Ready for Validation until the source-system change is complete.",
        ],
      };
    }

    if (visibleModalTaskStatus === "in_progress") {
      if (isCodeEdgeDraft && codeEdgeContext) {
        return {
          tone: "ok",
          title: "Work started. Complete the source-system remediation next.",
          detail: `Open ${codeEdgeSourceLabel} and remediate ${codeEdgeContext.setting} before requesting validation.`,
          steps: [
            `Open ${codeEdgeSourceLabel} and navigate to ${codeEdgeContext.sourcePath}.`,
            codeEdgeContext.action,
            `Confirm the affected resource is ${codeEdgeResource}.`,
            `Capture before/after evidence showing ${codeEdgeContext.evidenceTarget}.`,
            `When the source fix is complete, click Ready for Validation, then rerun the ${codeEdgeSyncLabel}, normalize ${codeEdgeNormalizePayload}, and refresh posture/freshness.`,
          ],
        };
      }

      return {
        tone: "ok",
        title: "Work started. Complete the source-system remediation next.",
        detail:
          "Go to AWS IAM and update the affected role trust relationship before requesting validation.",
        steps: [
          `Open AWS IAM → Roles → ${roleTarget}.`,
          "Open the Trust relationships tab and review the trusted external account principals.",
          "Restrict unapproved principals, add required external ID/conditions, and verify least-privilege permissions.",
          "Save evidence of the before/after trust policy and CloudTrail AssumeRole review.",
          "When the source fix is complete, click Ready for Validation, then run Full Cloud Assessment.",
        ],
      };
    }

    if (visibleModalTaskStatus === "ready_for_validation") {
      if (isCodeEdgeDraft && codeEdgeContext) {
        return {
          tone: "ok",
          title: "Task is ready for validation.",
          detail: `Validate this task from refreshed ${codeEdgeSourceLabel} connector evidence before closure.`,
          steps: [
            `Rerun the ${codeEdgeSyncLabel}.`,
            `Rerun OASYS normalization for ${codeEdgeNormalizePayload}.`,
            "Refresh posture/freshness after normalization completes.",
            `Confirm ${ruleId} is pass, cleared, or no longer failed for ${codeEdgeResource}.`,
            "If validation passes, close the task. If it fails, reopen the Fix Plan and complete the remaining source-system fix.",
          ],
        };
      }

      return {
        tone: "ok",
        title: "Task is ready for validation.",
        detail: "Run Full Cloud Assessment so OASYS can verify the source evidence.",
        steps: [
          "Click Run Full Cloud Assessment below.",
          "Wait for the IAM sync and posture recalculation to finish.",
          "If validation passes, close the task. If it fails, reopen the Fix Plan and complete the remaining source fix.",
        ],
      };
    }

    if (visibleModalTaskStatus === "blocked") {
      return {
        tone: "warn",
        title: "Task is blocked.",
        detail: "Resolve the blocker, then resume work before moving to validation.",
        steps: [
          "Review the block reason in the remediation task.",
          "Resolve the dependency or missing approval.",
          "Click Resume Work when the blocker is cleared.",
        ],
      };
    }

    return {
      tone: "ok",
      title: "Copilot draft applied to tracked task.",
      detail: "Continue the remediation workflow using the task action buttons below.",
      steps: [],
    };
  }, [
    effectiveDraftApplied,
    fixPlanAwsContext,
    fixPlanItem,
    visibleModalTask,
    visibleModalTaskStatus,
  ]);

  useEffect(() => {
    if (typeof setCopilotContext !== "function") return undefined;

    const frameworkValue =
      cleanValue(fixPlanItem?.framework, "") || cleanValue(framework, "");
    const controlId = cleanValue(fixPlanItem?.control_id, "");
    const ruleId = cleanValue(fixPlanItem?.rule_id, "");
    const resourceId =
      cleanValue(fixPlanItem?.resource_id, "") ||
      cleanValue(fixPlanAwsContext?.resourceId, "") ||
      cleanValue(fixPlanAwsContext?.roleArn, "") ||
      cleanValue(fixPlanAwsContext?.userArn, "");
    const taskStatus = visibleModalTaskStatus || "not_created";
    const validationStatus = cleanValue(visibleModalTask?.validation_status, "pending");
    const linkedSopStatus = fixPlanRelatedSops.length > 0 ? "linked" : "none";
    const copilotDraftStatus = effectiveDraftApplied
      ? "applied"
      : effectiveRemediationDraft
      ? "drafted"
      : "none";

    setCopilotContext({
      page: "posture",
      mode: "compliance",
      surface: "fix_plan",
      context_label: buildControlLabel(controlId, ruleId),
      framework: frameworkValue,
      control_id: controlId,
      rule_id: ruleId,
      resource_id: resourceId,
      task_id: getTaskId(visibleModalTask),
      task_status: taskStatus,
      validation_status: validationStatus,
      linked_sop_status: linkedSopStatus,
      linked_sop_count: fixPlanRelatedSops.length,
      copilot_draft_status: copilotDraftStatus,
      copilot_pathway_id: effectiveRemediationDraft?.pathway_id || "",
      copilot_pathway_name: effectiveRemediationDraft?.pathway_name || "",
      next_step_title: copilotNextStepGuidance?.title || "",
      next_step: copilotNextStepGuidance?.detail || "",
      source_system: fixPlanAwsContext ? "aws_iam" : "posture",
      aws_identity:
        fixPlanAwsContext?.identityName ||
        fixPlanAwsContext?.roleName ||
        fixPlanAwsContext?.userName ||
        "",
      aws_policy:
        fixPlanAwsContext?.policyArn ||
        fixPlanAwsContext?.policyName ||
        fixPlanPolicyValue ||
        "",
    });

    return () => {
      if (typeof clearCopilotContext === "function") {
        clearCopilotContext();
      }
    };
  }, [
    clearCopilotContext,
    copilotNextStepGuidance,
    effectiveDraftApplied,
    effectiveRemediationDraft,
    fixPlanAwsContext,
    fixPlanItem,
    fixPlanPolicyValue,
    fixPlanRelatedSops.length,
    framework,
    setCopilotContext,
    visibleModalTask,
    visibleModalTaskStatus,
  ]);

  useEffect(() => {
    if (
      !visibleModalTask ||
      visibleModalTaskStatus !== "in_progress" ||
      typeof registerCopilotWorkflowAction !== "function" ||
      typeof handleMarkTaskReadyForValidation !== "function"
    ) {
      return undefined;
    }

    return registerCopilotWorkflowAction(
      "remediation.mark_ready_for_validation",
      async () => {
        await handleMarkTaskReadyForValidation(visibleModalTask);
        return { ok: true, task_id: getTaskId(visibleModalTask) };
      },
      {
        label: "Mark Ready for Validation",
        surface: "fix_plan",
        task_id: getTaskId(visibleModalTask),
        task_status: visibleModalTaskStatus,
        enabled: !remediationTaskUpdating,
        description:
          "Marks the active remediation task Ready for Validation after the source-system fix and evidence are complete.",
      }
    );
  }, [
    handleMarkTaskReadyForValidation,
    registerCopilotWorkflowAction,
    remediationTaskUpdating,
    visibleModalTask,
    visibleModalTaskStatus,
  ]);

  useEffect(() => {
    if (
      !visibleModalTask ||
      visibleModalTaskStatus !== "ready_for_validation" ||
      typeof registerCopilotWorkflowAction !== "function" ||
      typeof handleRunFullCloudAssessment !== "function"
    ) {
      return undefined;
    }

    return registerCopilotWorkflowAction(
      "assessment.run_full_cloud_assessment",
      async () => {
        await handleRunFullCloudAssessment();
        return { ok: true, task_id: getTaskId(visibleModalTask) };
      },
      {
        label: "Run Full Cloud Assessment",
        surface: "fix_plan",
        task_id: getTaskId(visibleModalTask),
        task_status: visibleModalTaskStatus,
        enabled: !fullAssessmentRunning,
        description:
          "Runs the active Full Cloud Assessment so OASYS can sync source evidence, recalculate posture, and validate the task.",
      }
    );
  }, [
    fullAssessmentRunning,
    handleRunFullCloudAssessment,
    registerCopilotWorkflowAction,
    visibleModalTask,
    visibleModalTaskStatus,
  ]);

  const draftFindingSummary = useMemo(
    () =>
      cleanValue(fixPlanItem?.reason_summary, "") ||
      cleanValue(enrichedFixPlanItem?.reason_summary, "") ||
      cleanValue(relatedSopRemediation?.finding_summary, "") ||
      `Control ${cleanValue(fixPlanItem?.control_id, "")} is failing for rule ${cleanValue(
        fixPlanItem?.rule_id,
        "unknown"
      )}.`,
    [fixPlanItem, enrichedFixPlanItem, relatedSopRemediation]
  );

  const handleDraftRemediationPlan = useCallback(async () => {
    if (!fixPlanItem) return;

    setRemediationDraftError("");
    setRemediationDraftNotice("");
    setRemediationDraftApplied(false);
    setRemediationDraftLoading(true);

    try {
      const token = await getCopilotToken(getAccessToken);
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) headers.Authorization = `Bearer ${token}`;
      if (effectiveTenantId) headers["X-Tenant-Id"] = effectiveTenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userName) headers["X-User-Name"] = userName;

      const payload = {
        framework: fixPlanItem.framework || framework || null,
        control_id: fixPlanItem.control_id,
        rule_id: fixPlanItem.rule_id || null,
        finding_id: fixPlanItem.finding_id || fixPlanItem.id || null,
        finding_summary: draftFindingSummary,
        linked_sop_id: primarySopId || null,
        task_id: visibleModalTask?.id || null,
        resource_id: fixPlanItem.resource_id || fixPlanAwsContext?.resourceId || null,
        source: "copilot",
        context: {
          opened_from: "fix_plan",
          aws_context: fixPlanAwsContext || null,
          existing_task_status: visibleModalTask?.status || null,
          validation_status: visibleModalTask?.validation_status || null,
          fix_plan_steps: Array.isArray(fixPlanSteps) ? fixPlanSteps : [],
          fix_plan_validation: fixPlanValidation || null,
          linked_sop_count: Array.isArray(fixPlanRelatedSops) ? fixPlanRelatedSops.length : 0,
          pathway_hint: relatedSopRemediation?.pathway_id || null,
        },
      };

      const response = await fetch(`${effectiveApiBaseUrl}/remediation/draft`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail = data?.detail || data?.message;
        const message = Array.isArray(detail)
          ? detail.map((entry) => `${Array.isArray(entry?.loc) ? entry.loc.join(".") : "request"}: ${entry?.msg || JSON.stringify(entry)}`).join("; ")
          : typeof detail === "string" ? detail
          : detail && typeof detail === "object" ? JSON.stringify(detail)
          : `Draft request failed (${response.status}).`;
        throw new Error(message);
      }

      const enrichedDraft = mergeCopilotDraftWithRuleSpecificContext({
        baseDraft: data?.draft || data,
        fixPlanItem,
        enrichedFixPlanItem,
        fixPlanAwsContext,
        fixPlanSteps,
        fixPlanValidation,
        fixPlanRelatedSops,
        relatedSopRemediation,
        visibleModalTask,
        isCredentialReport: isFixPlanCredentialReport,
        isAccessKey: isFixPlanAccessKey,
        isActiveAccessKey: isFixPlanActiveAccessKey,
        isInlinePolicy: isFixPlanInlinePolicy,
      });

      setRemediationDraft(enrichedDraft);
      setRemediationDraftNotice(
        hasAppliedCopilotDraft
          ? "Revised rule-specific Copilot draft generated. Review before applying it to the tracked task."
          : "Rule-specific Copilot draft generated. Review before applying to a tracked task."
      );
    } catch (error) {
      console.error("Failed to draft remediation plan:", error);
      setRemediationDraftError(String(error?.message || error || "Failed to draft remediation plan."));
    } finally {
      setRemediationDraftLoading(false);
    }
  }, [
    fixPlanItem,
    getAccessToken,
    effectiveTenantId,
    userId,
    userName,
    framework,
    draftFindingSummary,
    primarySopId,
    visibleModalTask,
    fixPlanAwsContext,
    fixPlanSteps,
    fixPlanValidation,
    fixPlanRelatedSops,
    relatedSopRemediation,
    enrichedFixPlanItem,
    effectiveApiBaseUrl,
    hasAppliedCopilotDraft,
    isFixPlanCredentialReport,
    isFixPlanAccessKey,
    isFixPlanActiveAccessKey,
    isFixPlanInlinePolicy,
  ]);

  const handleDiscardDraft = useCallback(() => {
    setRemediationDraft(null);
    setRemediationDraftError("");
    setRemediationDraftNotice("");
    setRemediationDraftApplied(false);
  }, []);

  const handleCopyDraft = useCallback(async () => {
    if (!effectiveRemediationDraft) return;

    const text = JSON.stringify(effectiveRemediationDraft, null, 2);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setRemediationDraftNotice("Copilot draft copied to clipboard.");
        return;
      }
    } catch (error) {
      console.warn("Clipboard copy failed:", error);
    }

    setRemediationDraftNotice("Copy unavailable in this browser. Select and copy the draft manually.");
  }, [effectiveRemediationDraft]);

  const buildDraftTaskUpdatePayload = useCallback(() => {
    const draftForUpdate = remediationDraft || effectiveRemediationDraft;
    if (!visibleModalTask || !draftForUpdate) return null;

    const existingMetadata = visibleModalTask.task_metadata || {};
    const linkedSopId =
      draftForUpdate.linked_sop_id ||
      primarySopId ||
      visibleModalTask.linked_sop_id ||
      null;

    return {
      title: draftForUpdate.title || visibleModalTask.title,
      description:
        draftForUpdate.investigation_notes ||
        draftForUpdate.finding_summary ||
        visibleModalTask.description,
      remediation_steps:
        draftForUpdate.recommended_actions ||
        visibleModalTask.remediation_steps ||
        [],
      linked_sop_id: linkedSopId,
      task_metadata: {
        ...existingMetadata,
        copilot_draft: {
          draft_type: "remediation_plan",
          generated_from: "fix_plan",
          generated_at: new Date().toISOString(),
          pathway_id: draftForUpdate.pathway_id || null,
          pathway_name: draftForUpdate.pathway_name || null,
          evidence_checklist: draftForUpdate.evidence_checklist || [],
          validation_steps: draftForUpdate.validation_steps || [],
          risk_notes: draftForUpdate.risk_notes || [],
          draft_metadata: draftForUpdate.draft_metadata || {},
        },
      },
    };
  }, [visibleModalTask, remediationDraft, effectiveRemediationDraft, primarySopId]);

  const handleApplyDraftToTask = useCallback(async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (event?.stopPropagation) event.stopPropagation();

    setRemediationDraftApplying(true);
    setRemediationDraftError("");
    setRemediationDraftNotice("Preparing Copilot draft update…");

    const taskId = getTaskId(visibleModalTask);

    if (!taskId || !effectiveRemediationDraft) {
      setRemediationDraftApplying(false);
      setRemediationDraftNotice("");
      setRemediationDraftError(
        !taskId
          ? "The tracked remediation task is missing an ID. Close and reopen the Fix Plan, then try again."
          : "No Copilot draft is available to apply. Generate the draft again, then apply it to the task."
      );
      return;
    }

    const updates = buildDraftTaskUpdatePayload();
    if (!updates) {
      setRemediationDraftApplying(false);
      setRemediationDraftNotice("");
      setRemediationDraftError("Unable to build the task update payload from this draft.");
      return;
    }

    try {
      const token = await getCopilotToken(getAccessToken);
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) headers.Authorization = `Bearer ${token}`;
      if (effectiveTenantId) headers["X-Tenant-Id"] = effectiveTenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userName) headers["X-User-Name"] = userName;

      const response = await fetch(
        `${effectiveApiBaseUrl}/remediation/tasks/${encodeURIComponent(taskId)}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(updates),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            data?.message ||
            `Apply draft request failed (${response.status}).`
        );
      }

      const updatedTask =
        data?.task ||
        data?.updated_task ||
        data?.remediation_task ||
        (data?.id ? data : null);

      if (updatedTask && typeof onRemediationTaskUpdated === "function") {
        onRemediationTaskUpdated(updatedTask);
      }

      setRemediationDraftApplied(true);
      setRemediationDraftNotice("Copilot remediation draft applied to tracked task. Next step: start work.");
      setRemediationDraftError("");
    } catch (error) {
      console.error("Failed to apply Copilot draft to task:", error);
      setRemediationDraftError(
        String(error?.message || error || "Failed to apply Copilot draft to task.")
      );
      setRemediationDraftNotice("");
    } finally {
      setRemediationDraftApplying(false);
    }
  }, [
    visibleModalTask,
    effectiveRemediationDraft,
    buildDraftTaskUpdatePayload,
    onRemediationTaskUpdated,
    getAccessToken,
    effectiveTenantId,
    userId,
    userName,
    effectiveApiBaseUrl,
  ]);


  const fetchApprovedExceptionEvidenceCount = useCallback(async (taskId) => {
    if (!taskId) {
      setApprovedExceptionEvidenceCount(0);
      return 0;
    }

    setApprovedExceptionEvidenceLoading(true);

    try {
      const token = await getCopilotToken(getAccessToken);
      const headers = {};

      if (token) headers.Authorization = `Bearer ${token}`;
      if (effectiveTenantId) headers["X-Tenant-Id"] = effectiveTenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userName) headers["X-User-Name"] = userName;

      const response = await fetch(
        `${effectiveApiBaseUrl}/remediation/tasks/${encodeURIComponent(taskId)}/evidence`,
        {
          method: "GET",
          headers,
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail = Array.isArray(data?.detail)
          ? data.detail.map((item) => item?.msg || item?.detail || JSON.stringify(item)).join(" ")
          : data?.detail;
        throw new Error(detail || data?.message || `Evidence check failed (${response.status}).`);
      }

      const count = normalizeEvidencePayload(data).length;
      setApprovedExceptionEvidenceCount(count);
      return count;
    } catch (error) {
      console.error("Failed to check approved-exception evidence:", error);
      setApprovedExceptionEvidenceCount(0);
      setApprovedExceptionError(
        String(error?.message || error || "Unable to confirm linked evidence for this task.")
      );
      return 0;
    } finally {
      setApprovedExceptionEvidenceLoading(false);
    }
  }, [effectiveApiBaseUrl, effectiveTenantId, getAccessToken, userId, userName]);

  const handleApprovedExceptionFieldChange = useCallback((field, value) => {
    setApprovedExceptionForm((current) => ({
      ...current,
      [field]: value,
    }));
    setApprovedExceptionError("");
  }, []);

  const handleCloseApprovedExceptionForm = useCallback(() => {
    setApprovedExceptionOpen(false);
    setApprovedExceptionError("");
  }, []);

  const handleOpenApprovedExceptionForm = useCallback((event) => {
    if (event?.preventDefault) event.preventDefault();
    if (event?.stopPropagation) event.stopPropagation();

    if (approvedExceptionRecorded) {
      setApprovedExceptionOpen(false);
      setApprovedExceptionError("");
      setApprovedExceptionNotice("");

      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          approvedExceptionRecordedRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }, 80);
      });
      return;
    }

    const taskId = getTaskId(visibleModalTask);

    setApprovedExceptionOpen(true);
    setApprovedExceptionError("");
    setApprovedExceptionNotice("");

    if (taskId) {
      fetchApprovedExceptionEvidenceCount(taskId).then((count) => {
        if (count < 1) {
          setApprovedExceptionError(
            "Attach supporting evidence to this task before recording an approved exception. Use Review / Manage → Evidence, then return to Path B."
          );
        }
      });
    }

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        approvedExceptionFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);
    });
  }, [approvedExceptionRecorded, fetchApprovedExceptionEvidenceCount, visibleModalTask]);

  const handleSubmitApprovedException = useCallback(async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (event?.stopPropagation) event.stopPropagation();

    const taskId = getTaskId(visibleModalTask);

    if (approvedExceptionRecorded) {
      setApprovedExceptionOpen(false);
      setApprovedExceptionError("");
      setApprovedExceptionNotice("Approved exception is already recorded. Next step: mark the task Validated, then close it with a closure reason.");
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          approvedExceptionRecordedRef.current?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        }, 80);
      });
      return;
    }

    if (!taskId) {
      setApprovedExceptionError("Create or open the tracked remediation task before approving an exception.");
      return;
    }

    const requiredFields = [
      ["exception_reason", "Exception reason"],
      ["business_owner", "Decision owner / Approver"],
      ["business_purpose", "Justification / Purpose"],
      ["dependency_notes", "Dependency / impact notes"],
      ["access_advisor_review", "Access review"],
      ["cloudtrail_review", "Activity review"],
      ["review_date", "Review date"],
      ["evidence_ref", "Supporting evidence reference"],
    ];

    const missingFields = requiredFields
      .filter(([field]) => !cleanValue(approvedExceptionForm[field], ""))
      .map(([, label]) => label);

    if (missingFields.length > 0) {
      setApprovedExceptionError(`Approved exception requires: ${missingFields.join(", ")}.`);
      return;
    }

    const evidenceCount = await fetchApprovedExceptionEvidenceCount(taskId);
    if (evidenceCount < 1) {
      setApprovedExceptionError(
        "Approved exception blocked: attach at least one supporting evidence item to the task before recording this exception."
      );
      setApprovedExceptionNotice("");
      return;
    }

    setApprovedExceptionSubmitting(true);
    setApprovedExceptionError("");
    setApprovedExceptionNotice("Submitting approved exception…");

    try {
      const token = await getCopilotToken(getAccessToken);
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) headers.Authorization = `Bearer ${token}`;
      if (effectiveTenantId) headers["X-Tenant-Id"] = effectiveTenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userName) headers["X-User-Name"] = userName;

      const ruleId = cleanValue(fixPlanItem?.rule_id, "");
      const resourceId = cleanValue(
        fixPlanItem?.resource_id ||
          visibleModalTask?.resource_id ||
          fixPlanAwsContext?.resourceId ||
          fixPlanAwsContext?.roleName ||
          fixPlanAwsContext?.identityName,
        ""
      );
      const exceptionType = getApprovedExceptionType(ruleId);
      const decisionPath = getApprovedExceptionDecisionPath(ruleId);

      const payload = {
        validation_status: "approved_exception",
        validation_result: "approved_exception",
        validator_type: "exception",
        validator_name: "Approved Exception",
        reason: approvedExceptionForm.exception_reason,
        evidence_ref: approvedExceptionForm.evidence_ref,
        metadata: {
          exception_reason: approvedExceptionForm.exception_reason,
          business_owner: approvedExceptionForm.business_owner,
          business_purpose: approvedExceptionForm.business_purpose,
          dependency_notes: approvedExceptionForm.dependency_notes,
          access_advisor_review: approvedExceptionForm.access_advisor_review,
          cloudtrail_review: approvedExceptionForm.cloudtrail_review,
          review_date: approvedExceptionForm.review_date,
          exception_type: exceptionType,
          decision_path: decisionPath,
          validation_governance: "approved_exception",
          rule_id: ruleId,
          resource_id: resourceId,
          source_surface: "fix_plan_path_b",
        },
      };

      const response = await fetch(
        `${effectiveApiBaseUrl}/remediation/tasks/${encodeURIComponent(taskId)}/validate`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail = Array.isArray(data?.detail)
          ? data.detail.map((item) => item?.msg || item?.detail || JSON.stringify(item)).join(" ")
          : data?.detail;
        throw new Error(
          detail || data?.message || `Approved exception request failed (${response.status}).`
        );
      }

      const updatedTask = data?.task || data?.updated_task || data?.remediation_task || null;
      const nextTask = updatedTask || buildLocalApprovedExceptionTask(visibleModalTask, payload);
      if (nextTask && typeof onRemediationTaskUpdated === "function") {
        onRemediationTaskUpdated(nextTask);
      }

      setApprovedExceptionNotice("Approved exception recorded. Next step: mark the task Validated, then close it with a closure reason.");
      setApprovedExceptionError("");
      setApprovedExceptionOpen(false);
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          approvedExceptionRecordedRef.current?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        }, 120);
      });
    } catch (error) {
      console.error("Failed to submit approved exception:", error);
      setApprovedExceptionError(
        String(error?.message || error || "Failed to submit approved exception.")
      );
      setApprovedExceptionNotice("");
    } finally {
      setApprovedExceptionSubmitting(false);
    }
  }, [
    approvedExceptionForm,
    approvedExceptionRecorded,
    fetchApprovedExceptionEvidenceCount,
    effectiveApiBaseUrl,
    effectiveTenantId,
    fixPlanAwsContext,
    fixPlanItem,
    getAccessToken,
    onRemediationTaskUpdated,
    userId,
    userName,
    visibleModalTask,
  ]);

  const fixPlanRuleId = cleanValue(fixPlanItem?.rule_id, "");
  const fixPlanResourceType = cleanValue(
    fixPlanItem?.resource_type ||
      enrichedFixPlanItem?.resource_type ||
      visibleModalTask?.resource_type,
    ""
  );
  const isFixPlanEc2 = String(fixPlanRuleId || "").toUpperCase().startsWith("EC2_");
  const ec2ResourceId = cleanValue(fixPlanItem?.resource_id || enrichedFixPlanItem?.resource_id || visibleModalTask?.resource_id, "unavailable");
  const ec2IsEbs = fixPlanRuleId === "EC2_EBS_ENCRYPTION_ENABLED";
  const ec2ResourceType = ec2IsEbs ? "EBS volume" : "EC2 instance";
  const ec2LastSeen = fixPlanItem?.last_seen_at || enrichedFixPlanItem?.last_seen_at || visibleModalTask?.last_seen_at || null;
  const isFixPlanS3Posture = isS3PostureRule(fixPlanRuleId, fixPlanResourceType);
  const s3BucketName = cleanValue(
    fixPlanItem?.resource_id ||
      enrichedFixPlanItem?.resource_id ||
      visibleModalTask?.resource_id ||
      fixPlanAwsContext?.resourceId,
    ""
  );
  const s3SettingLabel = getS3SettingLabel(fixPlanRuleId);
  const s3ExpectedStateLabel = getS3ExpectedStateLabel(fixPlanRuleId);
  const s3PassedEvidenceText = getS3PassedEvidenceText(fixPlanRuleId, s3BucketName);
  const isFixPlanIamUnusedRole = isIamUnusedRoleRule(fixPlanRuleId);
  const isFixPlanIamTrustPolicy = isIamTrustPolicyRule(fixPlanRuleId);
  const isFixPlanIamPolicyEvent = isIamPolicyEventRule(fixPlanRuleId);
  const iamResourceCardTitle = getIamResourceCardTitle({
    isCredentialReport: isFixPlanCredentialReport,
    isUnusedRole: isFixPlanIamUnusedRole,
    isTrustPolicy: isFixPlanIamTrustPolicy,
    isPolicyEvent: isFixPlanIamPolicyEvent,
    isInlinePolicy: isFixPlanInlinePolicy,
  });
  const isFixPlanIdentity = isIdentityFixPlanRule(
    fixPlanRuleId,
    fixPlanItem,
    enrichedFixPlanItem,
    visibleModalTask
  );
  const identityFixPlanContext = isFixPlanIdentity
    ? getIdentityFixPlanContext({
        ruleId: fixPlanRuleId,
        fixPlanItem,
        enrichedFixPlanItem,
        visibleModalTask,
      })
    : null;
  const fixPlanRaw = fixPlanItem?.raw || {};
  const enrichedFixPlanRaw = enrichedFixPlanItem?.raw || {};
  const fixPlanSourceText = normalizeText(
    [
      fixPlanItem?.source,
      fixPlanItem?.provider,
      fixPlanItem?.stream,
      fixPlanItem?.resource_type,
      enrichedFixPlanItem?.source,
      enrichedFixPlanItem?.provider,
      enrichedFixPlanItem?.stream,
      enrichedFixPlanItem?.resource_type,
      visibleModalTask?.source,
      visibleModalTask?.stream,
      visibleModalTask?.resource_type,
      fixPlanRaw?.source,
      fixPlanRaw?.stream,
      fixPlanRaw?.resource_type,
      enrichedFixPlanRaw?.source,
      enrichedFixPlanRaw?.stream,
      enrichedFixPlanRaw?.resource_type,
    ]
      .filter(Boolean)
      .join(" ")
  );
  const isFixPlanCloudflare =
    String(fixPlanRuleId || "").toUpperCase().startsWith("CF_") ||
    fixPlanSourceText.includes("cloudflare");
  const isFixPlanGithub =
    String(fixPlanRuleId || "").toUpperCase().startsWith("GH_") ||
    fixPlanSourceText.includes("github");
  const isFixPlanCodeEdge = isFixPlanCloudflare || isFixPlanGithub;
  const codeEdgeProviderLabel = isFixPlanGithub ? "GitHub" : isFixPlanCloudflare ? "Cloudflare" : "Code / Edge";
  const codeEdgeResourceId = cleanValue(
    enrichedFixPlanItem?.resource_id ||
      fixPlanItem?.resource_id ||
      visibleModalTask?.resource_id ||
      enrichedFixPlanRaw?.resource_id ||
      fixPlanRaw?.resource_id,
    isFixPlanGithub ? "affected GitHub repository" : "affected Cloudflare zone"
  );
  const codeEdgeStream = cleanValue(
    enrichedFixPlanItem?.stream ||
      fixPlanItem?.stream ||
      visibleModalTask?.stream ||
      enrichedFixPlanRaw?.stream ||
      fixPlanRaw?.stream,
    isFixPlanGithub ? "github_repos" : "cloudflare_default"
  );
  const codeEdgeLastSeen =
    enrichedFixPlanItem?.last_seen_at ||
    fixPlanItem?.last_seen_at ||
    visibleModalTask?.last_seen_at ||
    enrichedFixPlanItem?.last_checked_at ||
    fixPlanItem?.last_checked_at ||
    visibleModalTask?.last_checked_at ||
    enrichedFixPlanRaw?.last_seen_at ||
    fixPlanRaw?.last_seen_at ||
    null;
  const codeEdgeResourceType = getCodeEdgeResourceType(fixPlanRuleId, codeEdgeProviderLabel);
  const codeEdgeRequiredAction = getCodeEdgeRequiredAction(fixPlanRuleId, codeEdgeProviderLabel);
  const codeEdgeSourcePath = getCodeEdgeSourcePath(fixPlanRuleId, codeEdgeProviderLabel, codeEdgeResourceId);
  const codeEdgeResourceLabel = isFixPlanGithub ? "Repository" : isFixPlanCloudflare ? "Zone" : "Resource";
  const hasAwsResourceContext = hasMeaningfulSourceContext(fixPlanAwsContext);
  const shouldShowSourceResourceCard =
    isFixPlanCodeEdge || isFixPlanIdentity || isFixPlanS3Posture || isFixPlanEc2 || hasAwsResourceContext;

  const awsResourceCardTitle = isFixPlanCodeEdge
    ? isFixPlanGithub
      ? "Affected GitHub Code Resource"
      : "Affected Cloudflare Edge Resource"
    : isFixPlanIdentity
    ? identityFixPlanContext.title
    : isFixPlanS3Posture
    ? "Affected S3 Bucket"
    : isFixPlanEc2
    ? (ec2IsEbs ? "Affected EC2 EBS Volume" : "Affected EC2 Instance")
    : iamResourceCardTitle;
  const awsResourceCardBadge = isFixPlanCodeEdge
    ? isFixPlanGithub
      ? "GitHub Code"
      : "Cloudflare Edge"
    : isFixPlanIdentity
    ? identityFixPlanContext.badge
    : isFixPlanS3Posture
    ? "AWS S3"
    : isFixPlanEc2
    ? "AWS EC2"
    : "AWS IAM";
  const awsResourceCardCopy = isFixPlanCodeEdge
    ? `OASYS identified the exact ${codeEdgeProviderLabel} resource and setting tied to this risk, so remediation can be performed in the source system and validated from refreshed connector evidence.`
    : isFixPlanIdentity
    ? identityFixPlanContext.copy
    : isFixPlanS3Posture
    ? `OASYS identified the exact S3 bucket tied to this risk, so remediation can be validated against current bucket posture.`
    : isFixPlanEc2
    ? `OASYS identified ${ec2ResourceType.toLowerCase()} ${ec2ResourceId} for rule ${fixPlanRuleId}. Verify account, region and current settings in AWS before applying any approved change.`
    : getIamResourceCardCopy({
        isCredentialReport: isFixPlanCredentialReport,
        isUnusedRole: isFixPlanIamUnusedRole,
        isTrustPolicy: isFixPlanIamTrustPolicy,
        isPolicyEvent: isFixPlanIamPolicyEvent,
        isInlinePolicy: isFixPlanInlinePolicy,
        fallbackCopy: fixPlanAwsResourceCopy,
      });
  const awsResourcePath = isFixPlanCodeEdge
    ? codeEdgeSourcePath
    : isFixPlanIdentity
    ? identityFixPlanContext.path
    : isFixPlanS3Posture
    ? `Amazon S3 → Buckets → ${s3BucketName || "affected bucket"} → Properties`
    : isFixPlanEc2
    ? (ec2IsEbs ? `Amazon EC2 → Elastic Block Store → Volumes → ${ec2ResourceId}` : `Amazon EC2 → Instances → ${ec2ResourceId} → Actions → Instance settings → Modify instance metadata options`)
    : fixPlanAwsContext?.awsPath;
  const passedEvidenceText = isFixPlanCodeEdge
    ? `Latest ${codeEdgeProviderLabel} connector evidence shows this code/edge finding has been remediated.`
    : isFixPlanIdentity
    ? `Latest ${identityFixPlanContext.providerLabel} identity inventory shows this identity finding has been remediated.`
    : isFixPlanS3Posture
    ? s3PassedEvidenceText
    : fixPlanPassedEvidenceText;
  const shouldShowPassedEvidence =
    normalizeText(visibleModalTask?.validation_status) === "passed" &&
    (isFixPlanCodeEdge || isFixPlanIdentity || isFixPlanS3Posture || isFixPlanCredentialReport || fixPlanAwsContext?.attachedPolicyArns?.length === 0);
  const iamDecisionPanel = useMemo(
    () =>
      getIamDecisionPanelData({
        ruleId: fixPlanRuleId,
        awsContext: fixPlanAwsContext,
        isCredentialReport: isFixPlanCredentialReport,
        isAccessKey: isFixPlanAccessKey,
        isActiveAccessKey: isFixPlanActiveAccessKey,
        isInlinePolicy: isFixPlanInlinePolicy,
      }),
    [
      fixPlanRuleId,
      fixPlanAwsContext,
      isFixPlanCredentialReport,
      isFixPlanAccessKey,
      isFixPlanActiveAccessKey,
      isFixPlanInlinePolicy,
    ]
  );

  useEffect(() => {
    if (!iamDecisionPanel) return;

    setApprovedExceptionForm(
      buildDefaultApprovedExceptionForm({
        ruleId: fixPlanRuleId,
        resourceId:
          fixPlanItem?.resource_id ||
          visibleModalTask?.resource_id ||
          fixPlanAwsContext?.resourceId ||
          fixPlanAwsContext?.roleName ||
          fixPlanAwsContext?.identityName,
        iamDecisionPanel,
        visibleModalTask,
        userName,
      })
    );
  }, [
    fixPlanDraftResetKey,
    fixPlanItem?.resource_id,
    fixPlanRuleId,
    fixPlanAwsContext,
    iamDecisionPanel,
    userName,
    visibleModalTask,
  ]);


  if (!fixPlanItem) return null;

  return (
    <div
                role="dialog"
                aria-modal="true"
                aria-label="Fix plan"
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 80,
                  background: "rgba(2, 6, 23, 0.72)",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  padding: "72px 24px 24px",
                  overflowY: "auto",
                }}
                onClick={handleCloseFixPlan}
              >
                <section
                  className="card"
                  style={{
                    width: "min(1040px, calc(100vw - 48px))",
                    maxHeight: "calc(100vh - 96px)",
                    overflow: "auto",
                    boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="card-header">
                    <div>
                      <div className="dash-eyebrow">OASYS · Remediation Fix Plan</div>
                      <div className="card-title">
                        {buildControlLabel(fixPlanItem.control_id, fixPlanItem.rule_id)}
                      </div>
                      <div
                        style={{
                          color: "var(--muted)",
                          fontSize: 12,
                          lineHeight: 1.45,
                          marginTop: 4,
                        }}
                      >
                        Follow this plan in the source system, then rerun assessment to validate the result.
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                        <RulePlanDiagnosticPill diagnostic={fixPlanRulePlanDiagnostic} />
                      </div>
                    </div>

                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={handleCloseFixPlan}
                    >
                      Close
                    </button>
                  </div>

                  <div className="card-body" style={{ display: "grid", gap: 14 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 10,
                      }}
                    >
                      <MetricCard
                        label="Framework"
                        value={cleanValue(fixPlanItem.framework, framework)}
                      />
                      <MetricCard
                        label="State"
                        value={getFixPlanDisplayState(fixPlanItem, visibleModalTask)}
                        tone={getFixPlanDisplayStateTone(visibleModalTask, fixPlanItem.state)}
                      />
                      <MetricCard
                        label="Freshness"
                        value={cleanValue(fixPlanItem.freshness_state)}
                        tone={normalizeText(fixPlanItem.freshness_state).includes("stale") ? "warn" : "default"}
                      />
                      <MetricCard
                        label="Open Findings"
                        value={cleanValue(fixPlanItem.open_findings_total, "—")}
                      />
                    </div>

                    {shouldShowSourceResourceCard ? (
                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--accent), transparent 72%)",
                          background: "color-mix(in srgb, var(--accent), transparent 94%)",
                          borderRadius: 16,
                          padding: 14,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: ".06em",
                                color: "var(--accent)",
                              }}
                            >
                              {awsResourceCardTitle}
                            </div>
                            <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.45, marginTop: 4 }}>
                              {awsResourceCardCopy}
                            </div>
                          </div>

                          <span
                            className="task-severity"
                            style={{
                              color: "var(--accent)",
                              background: "color-mix(in srgb, var(--accent), transparent 90%)",
                              border: "1px solid color-mix(in srgb, var(--accent), transparent 70%)",
                              borderRadius: 999,
                              padding: "4px 9px",
                              fontSize: 11,
                              fontWeight: 900,
                            }}
                          >
                            {awsResourceCardBadge}
                          </span>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 8,
                          }}
                        >
                          <MetaField label={isFixPlanCodeEdge || isFixPlanIdentity ? "Source Path" : "AWS Path"} value={awsResourcePath} wrap />
                          <MetaField
                            label={isFixPlanCodeEdge ? codeEdgeResourceLabel : isFixPlanIdentity ? "Identity Resource" : isFixPlanS3Posture ? "Bucket" : isFixPlanEc2 ? "Resource ID" : fixPlanIdentityLabel}
                            value={
                              isFixPlanCodeEdge
                                ? codeEdgeResourceId
                                : isFixPlanIdentity
                                ? identityFixPlanContext.resourceId
                                : isFixPlanS3Posture
                                ? s3BucketName
                                : isFixPlanEc2
                                ? ec2ResourceId
                                : fixPlanAwsContext.identityName ||
                                  fixPlanAwsContext.roleName ||
                                  fixPlanAwsContext.userName ||
                                  fixPlanAwsContext.groupName ||
                                  fixPlanAwsContext.roleArn ||
                                  fixPlanAwsContext.userArn
                            }
                            wrap
                          />
                          <MetaField
                            label={isFixPlanCodeEdge ? "Required Action" : isFixPlanIdentity ? "Required Action" : isFixPlanS3Posture ? "Control Setting" : isFixPlanEc2 ? "Required State" : fixPlanPolicyLabel}
                            value={isFixPlanCodeEdge ? codeEdgeRequiredAction : isFixPlanIdentity ? identityFixPlanContext.expectedAction : isFixPlanS3Posture ? s3SettingLabel : isFixPlanEc2 ? (ec2IsEbs ? "Encrypted EBS volume" : "IMDSv2 tokens required") : fixPlanPolicyValue}
                            wrap
                          />
                          <MetaField
                            label={isFixPlanCodeEdge ? "Resource Type" : isFixPlanIdentity ? "Resource Type" : isFixPlanS3Posture ? "Expected State" : isFixPlanEc2 ? "Resource Type" : isFixPlanCredentialReport ? "Resource" : "Policy ARN"}
                            value={isFixPlanCodeEdge ? codeEdgeResourceType : isFixPlanIdentity ? identityFixPlanContext.resourceType : isFixPlanS3Posture ? s3ExpectedStateLabel : isFixPlanEc2 ? ec2ResourceType : isFixPlanCredentialReport ? fixPlanAwsContext.resourceId : fixPlanAwsContext.policyArn}
                            wrap
                          />
                          <MetaField label={isFixPlanCodeEdge || isFixPlanIdentity ? "Rule" : isFixPlanS3Posture ? "Rule" : isFixPlanEc2 ? "Rule" : fixPlanEventLabel} value={isFixPlanCodeEdge ? fixPlanRuleId : isFixPlanIdentity ? identityFixPlanContext.ruleLabel : isFixPlanS3Posture ? fixPlanRuleId : isFixPlanEc2 ? fixPlanRuleId : fixPlanEventValue} />
                          <MetaField label={isFixPlanCodeEdge || isFixPlanIdentity ? "Provider" : isFixPlanS3Posture ? "Resource Type" : isFixPlanEc2 ? "Verification" : "Actor"} value={isFixPlanCodeEdge ? codeEdgeProviderLabel : isFixPlanIdentity ? identityFixPlanContext.providerLabel : isFixPlanS3Posture ? "S3 bucket" : isFixPlanEc2 ? "Verified EC2 evidence capture" : isFixPlanCredentialReport ? "—" : fixPlanAwsContext.actorName || "—"} />
                          <MetaField label={isFixPlanCodeEdge || isFixPlanIdentity || isFixPlanS3Posture || isFixPlanCredentialReport ? "Last Checked" : "Event Time"} value={isFixPlanCodeEdge ? formatTimestamp(codeEdgeLastSeen) : isFixPlanIdentity ? formatTimestamp(identityFixPlanContext.lastCheckedAt) : isFixPlanS3Posture || isFixPlanEc2 || isFixPlanCredentialReport ? formatTimestamp((isFixPlanEc2 ? ec2LastSeen : (enrichedFixPlanItem || fixPlanItem)?.last_checked_at)) : formatTimestamp(fixPlanAwsContext.eventTime)} />
                          <MetaField label="Source" value={isFixPlanCodeEdge ? codeEdgeStream : isFixPlanIdentity ? identityFixPlanContext.stream : isFixPlanS3Posture ? "AWS S3 posture" : isFixPlanEc2 ? "AWS EC2 inventory" : fixPlanAwsContext.sourceTable || fixPlanAwsContext.validationMethod || "AWS evidence"} wrap />

                          {isFixPlanAccessKey ? (
                            <>
                              <MetaField label="Access Key Status" value={getCredentialStatusLabel(fixPlanAwsContext.accessKeyActive)} />
                              <MetaField label="Created / Rotated" value={formatCredentialTimestamp(fixPlanAwsContext.accessKeyLastRotated)} />
                              <MetaField label="Last Used" value={formatCredentialTimestamp(fixPlanAwsContext.accessKeyLastUsedDate)} />
                              <MetaField label="Last Used Service" value={cleanValue(fixPlanAwsContext.accessKeyLastUsedService)} />
                              <MetaField label="Last Used Region" value={cleanValue(fixPlanAwsContext.accessKeyLastUsedRegion)} />
                            </>
                          ) : null}
                        </div>

                        {isFixPlanActiveAccessKey ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--warn), transparent 70%)",
                              background: "color-mix(in srgb, var(--warn), transparent 92%)",
                              color: "var(--warn)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              fontSize: 12,
                              lineHeight: 1.45,
                              fontWeight: 800,
                            }}
                          >
                            Active key detected. Identify the dependent workload, secret store, or automation before deactivating or deleting this key.
                          </div>
                        ) : null}

                        {shouldShowPassedEvidence ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--ok), transparent 72%)",
                              background: "color-mix(in srgb, var(--ok), transparent 92%)",
                              color: "var(--ok)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              fontSize: 12,
                              lineHeight: 1.45,
                              fontWeight: 800,
                            }}
                          >
                            {passedEvidenceText}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {iamDecisionPanel ? (
                      <>
                        <IamDecisionPanel
                          panel={iamDecisionPanel}
                          onOpenApprovedException={handleOpenApprovedExceptionForm}
                          approvedExceptionDisabled={!getTaskId(visibleModalTask) || approvedExceptionSubmitting}
                          approvedExceptionButtonLabel={
                            approvedExceptionSubmitting
                              ? "Submitting exception…"
                              : approvedExceptionRecorded
                              ? "Approved exception recorded"
                              : "Start approved exception"
                          }
                        />

                        {approvedExceptionRecorded ? (
                          <div
                            ref={approvedExceptionRecordedRef}
                            style={{
                              border: "1px solid color-mix(in srgb, var(--ok), transparent 66%)",
                              background: "color-mix(in srgb, var(--ok), transparent 91%)",
                              borderRadius: 16,
                              padding: 14,
                              display: "grid",
                              gap: 12,
                            }}
                          >
                            <div style={{ display: "grid", gap: 6 }}>
                              <div
                                style={{
                                  color: "var(--ok)",
                                  fontSize: 12,
                                  fontWeight: 950,
                                  letterSpacing: ".06em",
                                  textTransform: "uppercase",
                                }}
                              >
                                Approved Exception Recorded
                              </div>
                              <div style={{ color: "var(--text)", fontWeight: 900, lineHeight: 1.45 }}>
                                This finding has already been retained through an approved, evidence-backed exception. Do not approve it again. Next step: mark the task Validated, then close it with a closure reason.
                              </div>
                              <span
                                className="task-severity"
                                style={{
                                  width: "fit-content",
                                  color: "var(--ok)",
                                  background: "color-mix(in srgb, var(--ok), transparent 88%)",
                                  border: "1px solid color-mix(in srgb, var(--ok), transparent 68%)",
                                  borderRadius: 999,
                                  padding: "4px 10px",
                                  fontSize: 11,
                                  fontWeight: 900,
                                }}
                              >
                                Saved
                              </span>
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                gap: 8,
                              }}
                            >
                              <MetaField label="Decision Path" value={formatApprovedExceptionLabel(approvedExceptionDecisionPath)} wrap />
                              <MetaField label="Decision Owner" value={approvedExceptionDecisionOwner} wrap />
                              <MetaField label="Review Date" value={approvedExceptionReviewDate} wrap />
                              <MetaField label="Evidence" value={approvedExceptionEvidenceRef} wrap />
                              <MetaField label="Validation" value="Approved Exception" wrap />
                              <MetaField label="Task Status" value={getTaskStatusLabel(visibleModalTask?.status || "ready_for_validation")} wrap />
                            </div>
                          </div>
                        ) : null}

                        {approvedExceptionNotice ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--ok), transparent 70%)",
                              background: "color-mix(in srgb, var(--ok), transparent 92%)",
                              color: "var(--ok)",
                              borderRadius: 12,
                              padding: "9px 11px",
                              fontSize: 12,
                              fontWeight: 800,
                              lineHeight: 1.45,
                            }}
                          >
                            {approvedExceptionNotice}
                          </div>
                        ) : null}

                        {approvedExceptionError ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--danger), transparent 68%)",
                              background: "color-mix(in srgb, var(--danger), transparent 92%)",
                              color: "var(--danger)",
                              borderRadius: 12,
                              padding: "9px 11px",
                              fontSize: 12,
                              fontWeight: 800,
                              lineHeight: 1.45,
                            }}
                          >
                            {approvedExceptionError}
                          </div>
                        ) : null}

                        {approvedExceptionOpen && !approvedExceptionRecorded ? (
                          <form
                            ref={approvedExceptionFormRef}
                            onSubmit={handleSubmitApprovedException}
                            style={{
                              border: "1px solid color-mix(in srgb, var(--warn), transparent 68%)",
                              background: "color-mix(in srgb, var(--warn), transparent 94%)",
                              borderRadius: 16,
                              padding: 14,
                              display: "grid",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                              }}
                            >
                              <div style={{ display: "grid", gap: 4 }}>
                                <div
                                  style={{
                                    color: "var(--warn)",
                                    fontSize: 11,
                                    fontWeight: 900,
                                    letterSpacing: ".06em",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Approved Exception Governance
                                </div>
                                <div style={{ color: "var(--text)", fontWeight: 950, fontSize: 16 }}>
                                  Approved Exception Governance
                                </div>
                                <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>
                                  Document why this finding is being retained, who approved it, what evidence supports it, and when it must be reviewed again.
                                </div>
                              </div>
                              <span
                                style={{
                                  color: "var(--warn)",
                                  background: "color-mix(in srgb, var(--warn), transparent 90%)",
                                  border: "1px solid color-mix(in srgb, var(--warn), transparent 70%)",
                                  borderRadius: 999,
                                  padding: "4px 9px",
                                  fontSize: 11,
                                  fontWeight: 900,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Requires Compliance Owner
                              </span>
                            </div>

                            <div
                              style={{
                                border: `1px solid color-mix(in srgb, ${approvedExceptionHasEvidence ? "var(--ok)" : "var(--warn)"}, transparent 70%)`,
                                background: `color-mix(in srgb, ${approvedExceptionHasEvidence ? "var(--ok)" : "var(--warn)"}, transparent 92%)`,
                                color: approvedExceptionHasEvidence ? "var(--ok)" : "var(--warn)",
                                borderRadius: 12,
                                padding: "9px 11px",
                                fontSize: 12,
                                fontWeight: 850,
                                lineHeight: 1.45,
                              }}
                            >
                              <strong>Evidence required before approval:</strong>{" "}
                              {approvedExceptionEvidenceLoading
                                ? "Checking linked evidence…"
                                : approvedExceptionHasEvidence
                                ? `${approvedExceptionKnownEvidenceCount} linked evidence item${Number(approvedExceptionKnownEvidenceCount) === 1 ? "" : "s"} found. Approved exception can be recorded after the required fields are completed.`
                                : "Attach at least one supporting evidence item in Review / Manage → Evidence before recording this approved exception."}
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                gap: 10,
                              }}
                            >
                              <label style={{ display: "grid", gap: 5 }}>
                                <span className="open-item-label">Decision owner / Approver *</span>
                                <input
                                  value={approvedExceptionForm.business_owner || ""}
                                  onChange={(event) => handleApprovedExceptionFieldChange("business_owner", event.target.value)}
                                  placeholder="Example: Compliance Owner, system owner, control owner, or application owner"
                                  style={APPROVED_EXCEPTION_INPUT_STYLE}
                                />
                              </label>
                              <label style={{ display: "grid", gap: 5 }}>
                                <span className="open-item-label">Review date *</span>
                                <input
                                  type="date"
                                  value={approvedExceptionForm.review_date || ""}
                                  onChange={(event) => handleApprovedExceptionFieldChange("review_date", event.target.value)}
                                  style={APPROVED_EXCEPTION_INPUT_STYLE}
                                />
                              </label>
                            </div>

                            <label style={{ display: "grid", gap: 5 }}>
                              <span className="open-item-label">Exception reason *</span>
                              <textarea
                                value={approvedExceptionForm.exception_reason || ""}
                                onChange={(event) => handleApprovedExceptionFieldChange("exception_reason", event.target.value)}
                                rows={3}
                                placeholder="Explain why this finding is being retained as an approved exception."
                                style={APPROVED_EXCEPTION_TEXTAREA_STYLE}
                              />
                            </label>

                            <label style={{ display: "grid", gap: 5 }}>
                              <span className="open-item-label">Justification / Purpose *</span>
                              <textarea
                                value={approvedExceptionForm.business_purpose || ""}
                                onChange={(event) => handleApprovedExceptionFieldChange("business_purpose", event.target.value)}
                                rows={3}
                                placeholder="Example: This role supports CloudTrail-to-CloudWatch logging and is required for audit log delivery."
                                style={APPROVED_EXCEPTION_TEXTAREA_STYLE}
                              />
                            </label>

                            <label style={{ display: "grid", gap: 5 }}>
                              <span className="open-item-label">Dependency / impact notes *</span>
                              <textarea
                                value={approvedExceptionForm.dependency_notes || ""}
                                onChange={(event) => handleApprovedExceptionFieldChange("dependency_notes", event.target.value)}
                                rows={3}
                                placeholder="Example: Removing this role may disrupt logging, deployment automation, integrations, or rollback paths."
                                style={APPROVED_EXCEPTION_TEXTAREA_STYLE}
                              />
                            </label>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                gap: 10,
                              }}
                            >
                              <label style={{ display: "grid", gap: 5 }}>
                                <span className="open-item-label">Access review *</span>
                                <textarea
                                  value={approvedExceptionForm.access_advisor_review || ""}
                                  onChange={(event) => handleApprovedExceptionFieldChange("access_advisor_review", event.target.value)}
                                  rows={3}
                                  placeholder="Example: Access Advisor / permission review completed; summarize result or reference attached evidence."
                                  style={APPROVED_EXCEPTION_TEXTAREA_STYLE}
                                />
                              </label>
                              <label style={{ display: "grid", gap: 5 }}>
                                <span className="open-item-label">Activity review *</span>
                                <textarea
                                  value={approvedExceptionForm.cloudtrail_review || ""}
                                  onChange={(event) => handleApprovedExceptionFieldChange("cloudtrail_review", event.target.value)}
                                  rows={3}
                                  placeholder="Example: CloudTrail AssumeRole / activity review completed; summarize usage or dependency findings."
                                  style={APPROVED_EXCEPTION_TEXTAREA_STYLE}
                                />
                              </label>
                            </div>

                            <label style={{ display: "grid", gap: 5 }}>
                              <span className="open-item-label">Supporting evidence reference *</span>
                              <input
                                value={approvedExceptionForm.evidence_ref || ""}
                                onChange={(event) => handleApprovedExceptionFieldChange("evidence_ref", event.target.value)}
                                placeholder="Example: manual://approved-exception/iam-role-retention-review"
                                style={APPROVED_EXCEPTION_INPUT_STYLE}
                              />
                            </label>

                            <div
                              style={{
                                border: "1px solid color-mix(in srgb, var(--warn), transparent 72%)",
                                background: "color-mix(in srgb, var(--warn), transparent 92%)",
                                color: "var(--text)",
                                borderRadius: 12,
                                padding: "9px 11px",
                                fontSize: 12,
                                lineHeight: 1.45,
                              }}
                            >
                              <strong>Workflow:</strong> approved exception recorded → mark task Validated → close with
                              closure reason. The source finding may still exist, but the retained risk is documented,
                              approved, and time-bound.
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                              <button
                                type="button"
                                className="btn ghost"
                                onClick={handleCloseApprovedExceptionForm}
                                disabled={approvedExceptionSubmitting}
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="btn primary"
                                disabled={approvedExceptionSubmitting || approvedExceptionEvidenceLoading || approvedExceptionHasEvidence === false}
                                title={
                                  approvedExceptionEvidenceLoading
                                    ? "Checking linked evidence before approval."
                                    : approvedExceptionHasEvidence === false
                                    ? "Attach evidence in Review / Manage before recording this approved exception."
                                    : undefined
                                }
                              >
                                {approvedExceptionSubmitting
                                  ? "Submitting…"
                                  : approvedExceptionEvidenceLoading
                                  ? "Checking Evidence…"
                                  : "Record Approved Exception"}
                              </button>
                            </div>
                          </form>
                        ) : null}
                      </>
                    ) : null}

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                          borderRadius: 16,
                          padding: 14,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            color: "var(--muted)",
                          }}
                        >
                          Why this matters
                        </div>

                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text)" }}>
                          {fixPlanItem.reason_summary}
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gap: 8,
                            paddingTop: 4,
                          }}
                        >
                          <MetaField label="Control" value={cleanValue(fixPlanItem.control_id)} />
                          <MetaField label="Rule" value={cleanValue(fixPlanItem.rule_id)} />
                          <MetaField label="Resource" value={cleanValue(fixPlanItem.resource_id)} wrap />
                          <MetaField
                            label="Last Checked"
                            value={formatTimestamp(fixPlanItem.last_checked_at)}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                          borderRadius: 16,
                          padding: 14,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            color: "var(--muted)",
                          }}
                        >
                          Exact remediation steps
                        </div>

                        <ol
                          style={{
                            margin: 0,
                            paddingLeft: "1.1rem",
                            color: "var(--text)",
                            fontSize: 13,
                            lineHeight: 1.65,
                            display: "grid",
                            gap: 6,
                          }}
                        >
                          {fixPlanSteps.map((step, index) => (
                            <li key={`${step}-${index}`}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                        gap: 12,
                        alignItems: "start",
                      }}
                    >
                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                          borderRadius: 16,
                          padding: 14,
                          display: "grid",
                          gap: 10,
                          alignSelf: "start",
                          height: "fit-content",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            color: "var(--muted)",
                          }}
                        >
                          Governing SOP
                        </div>

                        {fixPlanRelatedSops.length === 0 ? (
                          <div
                            style={{
                              border: "1px dashed color-mix(in srgb, var(--text), transparent 82%)",
                              borderRadius: 12,
                              padding: "10px 12px",
                              color: "var(--muted)",
                              fontSize: 13,
                              lineHeight: 1.5,
                              alignSelf: "start",
                              height: "fit-content",
                            }}
                          >
                            No linked SOP is attached to this exact control/rule yet. Link one from SOP Center to make this remediation governed.
                          </div>
                        ) : (
                          <div style={{ display: "grid", gap: 10 }}>
                            {fixPlanRelatedSops.slice(0, 3).map((sop, index) => {
                              const sopId = sop.sop_id || sop.id || "";
                              const released = isReleasedGoverningSop(sop);
                              const releaseTone = released ? "var(--ok)" : "var(--warn)";

                              return (
                                <div
                                  key={`${sopId || index}`}
                                  className="open-item-pill"
                                  style={{
                                    cursor: "default",
                                    alignItems: "flex-start",
                                    alignSelf: "start",
                                    height: "auto",
                                    minHeight: "auto",
                                    display: "grid",
                                    gap: 8,
                                    borderColor: released
                                      ? "color-mix(in srgb, var(--ok), transparent 70%)"
                                      : "color-mix(in srgb, var(--warn), transparent 70%)",
                                    background: released
                                      ? "color-mix(in srgb, var(--ok), transparent 94%)"
                                      : "color-mix(in srgb, var(--warn), transparent 94%)",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      gap: 10,
                                      alignItems: "flex-start",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <div style={{ display: "grid", gap: 3, minWidth: 0 }}>
                                      <div style={{ fontWeight: 900, color: "var(--text)" }}>
                                        {getRelatedSopTitle(sop)}
                                      </div>
                                      <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
                                        {getRelatedSopVersionLabel(sop)} · {getSopStatusLabel(sop)}
                                      </div>
                                    </div>

                                    <span
                                      className="task-severity"
                                      style={{
                                        color: releaseTone,
                                        background: `color-mix(in srgb, ${releaseTone}, transparent 90%)`,
                                        border: `1px solid color-mix(in srgb, ${releaseTone}, transparent 70%)`,
                                        borderRadius: 999,
                                        padding: "4px 9px",
                                        fontSize: 11,
                                        fontWeight: 900,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {released ? "Released" : "Not Released"}
                                    </span>
                                  </div>

                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr)",
                                      gap: 6,
                                      fontSize: 12,
                                      lineHeight: 1.45,
                                    }}
                                  >
                                    <div style={{ color: "var(--muted)" }}>
                                      Linked to:{" "}
                                      <span style={{ color: "var(--text)", fontWeight: 800 }}>
                                        {getSopLinkScope(sop, fixPlanItem.framework || framework)}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        color: releaseTone,
                                        fontWeight: 800,
                                      }}
                                    >
                                      {getSopReleaseGuidance(sop)}
                                    </div>
                                  </div>

                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 2 }}>
                                    {sopId ? (
                                      <button
                                        className="btn btn-ghost btn-xs"
                                        type="button"
                                        onClick={() =>
                                          setMode("sop", {
                                            sopId,
                                            controlId: fixPlanItem.control_id,
                                            ruleId: fixPlanItem.rule_id || undefined,
                                            framework: fixPlanItem.framework || framework,
                                          })
                                        }
                                      >
                                        Open SOP
                                      </button>
                                    ) : null}

                                    <button
                                      className="btn btn-ghost btn-xs"
                                      type="button"
                                      onClick={() =>
                                        setMode("sop", {
                                          controlId: fixPlanItem.control_id,
                                          ruleId: fixPlanItem.rule_id || undefined,
                                          framework: fixPlanItem.framework || framework,
                                        })
                                      }
                                    >
                                      Go to SOP Center
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                          borderRadius: 16,
                          padding: 14,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            color: "var(--muted)",
                          }}
                        >
                          Validation
                        </div>

                        <div style={{ color: "var(--text)", fontSize: 13, lineHeight: 1.55 }}>
                          {fixPlanValidation}
                        </div>

                        {remediationTaskNotice ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--ok), transparent 72%)",
                              background: "color-mix(in srgb, var(--ok), transparent 92%)",
                              color: "var(--ok)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              fontSize: 12,
                              lineHeight: 1.45,
                              fontWeight: 800,
                            }}
                          >
                            {remediationTaskNotice}
                          </div>
                        ) : null}

                        {remediationTaskError ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--danger), transparent 72%)",
                              background: "color-mix(in srgb, var(--danger), transparent 92%)",
                              color: "var(--danger)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              fontSize: 12,
                              lineHeight: 1.45,
                              fontWeight: 800,
                            }}
                          >
                            {remediationTaskError}
                          </div>
                        ) : null}

                        {visibleModalTask ? (
                          <div
                            style={{
                              display: "grid",
                              gap: 6,
                              border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              fontSize: 12,
                              color: "var(--muted)",
                            }}
                          >
                            <MetaField label="Task ID" value={visibleModalTask.id} wrap />
                            <MetaField label="Task Status" value={getTaskStatusLabel(visibleModalTask.status)} />
                            <MetaField
                              label="Validation Status"
                              value={getTaskStatusLabel(visibleModalTask.validation_status || "pending")}
                            />
                            <MetaField label="Owner" value={visibleModalTask.owner_name || "Unassigned"} />
                          </div>
                        ) : null}

                        {remediationDraftNotice ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--ok), transparent 72%)",
                              background: "color-mix(in srgb, var(--ok), transparent 92%)",
                              color: "var(--ok)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              fontSize: 12,
                              lineHeight: 1.45,
                              fontWeight: 800,
                            }}
                          >
                            {remediationDraftNotice}
                          </div>
                        ) : null}

                        {remediationDraftError ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--danger), transparent 72%)",
                              background: "color-mix(in srgb, var(--danger), transparent 92%)",
                              color: "var(--danger)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              fontSize: 12,
                              lineHeight: 1.45,
                              fontWeight: 800,
                            }}
                          >
                            {remediationDraftError}
                          </div>
                        ) : null}

                        {effectiveRemediationDraft ? (
                          <div
                            style={{
                              border: "1px solid color-mix(in srgb, var(--accent), transparent 72%)",
                              background: "color-mix(in srgb, var(--accent), transparent 95%)",
                              borderRadius: 14,
                              padding: 12,
                              display: "grid",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                              }}
                            >
                              <div style={{ display: "grid", gap: 3 }}>
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                    letterSpacing: ".06em",
                                    color: "var(--accent)",
                                  }}
                                >
                                  OASYS Copilot Draft
                                </div>
                                <div style={{ color: "var(--text)", fontWeight: 900 }}>
                                  {effectiveRemediationDraft.title || "Draft remediation plan"}
                                </div>
                                <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
                                  {effectiveRemediationDraft.pathway_name || effectiveRemediationDraft.pathway_id || "Review-required remediation guidance"}
                                </div>
                              </div>

                              <span
                                className="task-severity"
                                style={{
                                  color: "var(--accent)",
                                  background: "color-mix(in srgb, var(--accent), transparent 90%)",
                                  border: "1px solid color-mix(in srgb, var(--accent), transparent 70%)",
                                  borderRadius: 999,
                                  padding: "4px 9px",
                                  fontSize: 11,
                                  fontWeight: 900,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Review Required
                              </span>
                            </div>

                            {effectiveRemediationDraft.investigation_notes ? (
                              <div style={{ color: "var(--text)", fontSize: 12, lineHeight: 1.55 }}>
                                {effectiveRemediationDraft.investigation_notes}
                              </div>
                            ) : null}

                            <DraftList title="Recommended Actions" items={effectiveRemediationDraft.recommended_actions} />
                            <DraftList title="Evidence Checklist" items={effectiveRemediationDraft.evidence_checklist} />
                            <DraftList title="Validation Steps" items={effectiveRemediationDraft.validation_steps} />
                            <DraftList title="Risk Notes" items={effectiveRemediationDraft.risk_notes} />

                            <div
                              style={{
                                border: "1px solid color-mix(in srgb, var(--accent), transparent 78%)",
                                background: "color-mix(in srgb, var(--accent), transparent 96%)",
                                borderRadius: 12,
                                padding: "10px 12px",
                                display: "grid",
                                gap: 6,
                              }}
                            >
                              <strong
                                style={{
                                  color:
                                    copilotNextStepGuidance.tone === "warn"
                                      ? "var(--warn)"
                                      : copilotNextStepGuidance.tone === "ok"
                                      ? "var(--ok)"
                                      : "var(--accent)",
                                  fontSize: 12,
                                }}
                              >
                                {copilotNextStepGuidance.title}
                              </strong>
                              <span style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
                                {copilotNextStepGuidance.detail}
                              </span>
                              {Array.isArray(copilotNextStepGuidance.steps) && copilotNextStepGuidance.steps.length > 0 ? (
                                <ol
                                  style={{
                                    margin: 0,
                                    paddingLeft: "1.1rem",
                                    color: "var(--text)",
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                    display: "grid",
                                    gap: 4,
                                  }}
                                >
                                  {copilotNextStepGuidance.steps.map((step, index) => (
                                    <li key={`copilot-next-step-${index}`}>{step}</li>
                                  ))}
                                </ol>
                              ) : null}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                                paddingTop: 2,
                              }}
                            >
                              {visibleModalTask ? (
                                <button
                                  className="btn btn-ghost btn-xs"
                                  type="button"
                                  onClick={handleApplyDraftToTask}
                                  disabled={remediationDraftApplying || effectiveDraftApplied}
                                  title={
                                    effectiveDraftApplied
                                      ? "This Copilot draft has already been applied to the tracked task"
                                      : hasAppliedCopilotDraft
                                      ? "Apply this revised Copilot draft to the existing remediation task"
                                      : "Apply this reviewed draft to the existing remediation task"
                                  }
                                >
                                  {remediationDraftApplying
                                    ? "Applying Draft…"
                                    : effectiveDraftApplied
                                    ? "Draft Applied"
                                    : hasAppliedCopilotDraft
                                    ? "Apply revised draft to task"
                                    : "Apply draft to task"}
                                </button>
                              ) : (
                                <button
                                  className="btn btn-ghost btn-xs"
                                  type="button"
                                  onClick={handleCreateRemediationTask}
                                  disabled={remediationTaskCreating}
                                  title="Create a tracked remediation task from this fix plan before applying the draft"
                                >
                                  {remediationTaskCreating ? "Creating Task…" : "Create task from this fix plan"}
                                </button>
                              )}

                              <button
                                className="btn btn-ghost btn-xs"
                                type="button"
                                onClick={handleCopyDraft}
                              >
                                Copy draft
                              </button>

                              {!effectiveDraftApplied ? (
                                <button
                                  className="btn btn-ghost btn-xs"
                                  type="button"
                                  onClick={handleDiscardDraft}
                                >
                                  Discard
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            paddingTop: 4,
                          }}
                        >
                          <button
                            className="btn btn-ghost btn-xs"
                            type="button"
                            onClick={handleCreateRemediationTask}
                            disabled={remediationTaskCreating || Boolean(visibleModalTask)}
                            title="Create a tracked remediation work item from this fix plan"
                          >
                            {remediationTaskCreating
                              ? "Creating Task…"
                              : visibleModalTask
                              ? "Task Exists"
                              : "Create Remediation Task"}
                          </button>

                          <button
                            className="btn btn-ghost btn-xs"
                            type="button"
                            onClick={handleDraftRemediationPlan}
                            disabled={remediationDraftLoading}
                            title={
                              hasAppliedCopilotDraft
                                ? "Generate a revised reviewable Copilot plan without overwriting the tracked task until you apply it"
                                : "Draft a reviewable, non-mutating remediation plan with OASYS Copilot"
                            }
                          >
                            {remediationDraftLoading
                              ? "Drafting Plan…"
                              : hasAppliedCopilotDraft
                              ? "Revise Copilot plan"
                              : "Draft remediation plan"}
                          </button>

                          {visibleModalTask && normalizeText(visibleModalTask.status) === "open" ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              type="button"
                              disabled={remediationTaskUpdating}
                              onClick={() =>
                                handleUpdateRemediationTask(
                                  visibleModalTask,
                                  {
                                    status: "in_progress",
                                    owner_user_id: looksLikeUuid(userId) ? userId : null,
                                    owner_name: userName || visibleModalTask.owner_name || "",
                                  },
                                  "Task started and marked In Progress."
                                )
                              }
                            >
                              Start Work
                            </button>
                          ) : null}

                          {visibleModalTask && normalizeText(visibleModalTask.status) === "in_progress" ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              type="button"
                              disabled={remediationTaskUpdating}
                              onClick={() => handleMarkTaskReadyForValidation(visibleModalTask)}
                            >
                              Ready for Validation
                            </button>
                          ) : null}

                          {visibleModalTask && ["open", "in_progress", "ready_for_validation"].includes(normalizeText(visibleModalTask.status)) ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              type="button"
                              disabled={remediationTaskUpdating}
                              onClick={() =>
                                handleUpdateRemediationTask(
                                  visibleModalTask,
                                  {
                                    status: "blocked",
                                    validation_status: "pending",
                                  },
                                  "Task marked Blocked."
                                )
                              }
                            >
                              Block Task
                            </button>
                          ) : null}

                          {visibleModalTask && normalizeText(visibleModalTask.status) === "blocked" ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              type="button"
                              disabled={remediationTaskUpdating}
                              onClick={() =>
                                handleUpdateRemediationTask(
                                  visibleModalTask,
                                  {
                                    status: "in_progress",
                                    validation_status: "pending",
                                  },
                                  "Task resumed and marked In Progress."
                                )
                              }
                            >
                              Resume Work
                            </button>
                          ) : null}

                          {visibleModalTask && normalizeText(visibleModalTask.status) === "validated" ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              type="button"
                              disabled={remediationTaskUpdating}
                              onClick={() =>
                                handleUpdateRemediationTask(
                                  visibleModalTask,
                                  {
                                    status: "closed",
                                    closure_reason: "Closed from OASYS Fix Plan.",
                                  },
                                  "Task closed."
                                )
                              }
                            >
                              Close Task
                            </button>
                          ) : null}

                          {relatedSopRemediation?.remediation_card?.open_sop_id ? (
                            <button
                              className="btn btn-ghost btn-xs"
                              type="button"
                              onClick={() =>
                                setMode("sop", {
                                  sopId: relatedSopRemediation.remediation_card.open_sop_id,
                                  controlId: fixPlanItem.control_id,
                                  ruleId: fixPlanItem.rule_id || undefined,
                                  framework: fixPlanItem.framework || framework,
                                })
                              }
                            >
                              Open governing SOP
                            </button>
                          ) : null}

                          <button
                            className="btn btn-ghost btn-xs"
                            type="button"
                            onClick={() =>
                              setMode("drift", {
                                framework: fixPlanItem.framework || framework,
                                controlId: fixPlanItem.control_id || undefined,
                                ruleId: fixPlanItem.rule_id || undefined,
                                resourceId: fixPlanItem.resource_id || undefined,
                              })
                            }
                          >
                            Open in Drift
                          </button>

                          <button
                            className="btn btn-ghost btn-xs"
                            type="button"
                            onClick={handleRunFullCloudAssessment}
                            disabled={fullAssessmentRunning}
                          >
                            {fullAssessmentRunning ? "Running Cloud Check…" : "Run Full Cloud Assessment"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
  );
}
